import { useState, useEffect } from 'react'
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'

// Declare custom element for TypeScript
declare global {
namespace JSX {
interface IntrinsicElements {
'api-sports-widget': any;
}
}
}

import { motion } from 'framer-motion'
import { useWallet, useConnection } from '@solana/wallet-adapter-react'
import { PublicKey, Transaction, TransactionInstruction, SystemProgram } from '@solana/web3.js'
import { TOKEN_PROGRAM_ID, ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddress, createAssociatedTokenAccountInstruction } from '@solana/spl-token'
import { useForm } from 'react-hook-form'
import axios from 'axios'
import toast from 'react-hot-toast'
import { 
Brain, 
TrendingUp, 
ArrowLeft,
DollarSign,
Users,
Clock,
Target,
Loader,
CheckCircle,
XCircle,
ExternalLink,
RefreshCw,
Award,
AlertTriangle,
Tv,
BarChart3,
MessageCircle,
FileText,
Activity,
Star,
Share2,
Radio,
Info,
X,
Sparkles,
Shield
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import MarketChat from '../components/MarketChat'
import ValidationPlanModal from '../components/ValidationPlanModal'
import ResolutionModal from '../components/ResolutionModal'
import AgiCreditBetting from '../components/AgiCreditBetting'
import { useAgiAuthStore, agiApi } from '../store/agiAuthStore'
import { config } from '../config'
import { useMarketPrice } from '../hooks/useMarketPrice'

interface Market {
id: number
question: string
category: string
description: string
totalYes: number
totalNo: number
total_yes?: number  // Backend might return snake_case
total_no?: number   // Backend might return snake_case
total_yes_bets?: number  // AGI market credit bets
total_no_bets?: number   // AGI market credit bets
closesAt: string
closes_at?: string  // Backend might return snake_case
betting_ends_at?: string // Backend database field - when betting closes
resolution_time?: string // Backend database field - when market resolves
resolved: boolean
winningOutcome?: number
outcome?: number // Backend returns this for resolved markets
adjudicator_reasoning?: string // Backend returns resolution explanation
resolution_confidence?: number
createdAt: string
created_at?: string // Backend might return snake_case
creator: string
creator_address?: string  // Backend returns this
creator_name?: string     // Backend returns this
creator_email?: string    // Backend returns this for AGI markets
status: 'active' | 'closed' | 'resolved' | 'disputed'
widget_config?: string // JSON string with live widget configuration
// AI Odds fields
ai_odds_yes?: number
ai_odds_no?: number
odds_reasoning?: string // JSON string
odds_reasoning_parsed?: {
  reasoning?: string
  researchSummary?: {
    currentSentiment?: string
    keyFactors?: string[]
    timelineCritical?: boolean
    lastUpdated?: string
  }
}
odds_confidence?: number
odds_sources?: string // JSON string
odds_sources_parsed?: Array<{
  type: string
  source: string
  relevance?: string
}>
odds_updated_at?: string
// News-generated market fields
source_article_url?: string
source_article_data?: string // JSON string with article details
// AGI Market fields
is_manual?: number  // 1 = AGI manual market
resolution_rules?: string
agi_odds_reasoning?: string
}

interface BetForm {
amount: number
outcome: number // 0 = NO, 1 = YES
}

export default function MarketDetailPage() {
const { id } = useParams()
const navigate = useNavigate()
const { connected, publicKey } = useWallet()
const { agiUser, isAgiAuthenticated, agiToken } = useAgiAuthStore()
const { connection } = useConnection()
const [market, setMarket] = useState<Market | null>(null)
const [loading, setLoading] = useState(true)
const [betting, setBetting] = useState(false)
const [selectedOutcome, setSelectedOutcome] = useState<number | null>(null)
const [showValidationPlan, setShowValidationPlan] = useState(false)
const [validationPlan, setValidationPlan] = useState(null)
const [showResolutionModal, setShowResolutionModal] = useState(false)
const [showOddsReasoningModal, setShowOddsReasoningModal] = useState(false)
const [resolutionEvidence, setResolutionEvidence] = useState<any[]>([])
const [widgetScriptLoaded, setWidgetScriptLoaded] = useState(false)
const [widgetHtml, setWidgetHtml] = useState<string>('')
const widgetContainerRef = React.useRef<HTMLDivElement>(null)
const [activeTab, setActiveTab] = useState<string>('odds') // Always default to odds
const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | 'all'>('24h') // For odds chart
const [bettors, setBettors] = useState<{yes: any[], no: any[]}>({yes: [], no: []})

// Admin: Regenerate AI odds
const [regenerateInstructions, setRegenerateInstructions] = useState('')
const [isRegeneratingOdds, setIsRegeneratingOdds] = useState(false)

// Add dynamic pricing
const { yesPercent, noPercent } = useMarketPrice(id || 0)

// Store price history for chart
const [priceHistory, setPriceHistory] = useState<Array<{time: string, yes: string, no: string}>>([])

// Track if price is rising or falling (simple!)
const [priceDirection, setPriceDirection] = useState<{yes: 'up' | 'down' | null, no: 'up' | 'down' | null}>({yes: null, no: null})

// Update price history and direction when price changes
useEffect(() => {
  if (yesPercent && noPercent) {
    const now = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    setPriceHistory(prev => {
      // Check if price went up or down by comparing to last entry
      if (prev.length > 0) {
        const lastPrice = prev[prev.length - 1];
        const lastYes = Number(lastPrice.yes);
        const lastNo = Number(lastPrice.no);
        const currentYes = Number(yesPercent);
        const currentNo = Number(noPercent);
        
        // Set direction based on change
        setPriceDirection({
          yes: currentYes > lastYes ? 'up' : currentYes < lastYes ? 'down' : null,
          no: currentNo > lastNo ? 'up' : currentNo < lastNo ? 'down' : null
        });
        
        // Clear direction after 3 seconds
        setTimeout(() => setPriceDirection({yes: null, no: null}), 3000);
      }
      
      // Keep last 20 price points
      const newHistory = [...prev, { time: now, yes: yesPercent, no: noPercent }].slice(-20);
      return newHistory;
    });
  }
}, [yesPercent, noPercent])
const [userIsWinner, setUserIsWinner] = useState(false)
const [checkingWinner, setCheckingWinner] = useState(true)
const [winningsClaimed, setWinningsClaimed] = useState(false)
const [checkingClaimed, setCheckingClaimed] = useState(true)
const [isFavorite, setIsFavorite] = useState(false)
const [monitoringLogs, setMonitoringLogs] = useState<any[]>([])
const [logsLoading, setLogsLoading] = useState(false)

// Fetch bettors when Stats tab is opened
useEffect(() => {
  if (activeTab === 'stats' && id && market) {
    // For AGI markets, fetch from AGI endpoint WITH auth token
    const isAgiMarket = market.is_manual === 1 || market.category === 'agi_manual'
    
    if (isAgiMarket && agiToken) {
      // AGI markets need authentication
      axios.get(`/api/agi/markets/${id}`, {
        headers: { Authorization: `Bearer ${agiToken}` }
      }).then(response => {
        const allBets = response.data.bets || []
        const yesBets = allBets.filter((bet: any) => bet.outcome === 1)
        const noBets = allBets.filter((bet: any) => bet.outcome === 0)
        setBettors({ yes: yesBets, no: noBets })
      }).catch(err => {
        console.error('Failed to fetch AGI bettors:', err)
      })
    } else if (!isAgiMarket) {
      // Regular markets
      axios.get(`/api/markets/${id}/bets`).then(response => {
        const allBets = response.data.bets || []
        const yesBets = allBets.filter((bet: any) => bet.outcome === 1)
        const noBets = allBets.filter((bet: any) => bet.outcome === 0)
        setBettors({ yes: yesBets, no: noBets })
      }).catch(err => {
        console.error('Failed to fetch bettors:', err)
      })
    }
  }
}, [activeTab, id, market, agiToken])

// Fetch monitoring logs when Event Monitor tab is opened + auto-refresh
useEffect(() => {
  if (activeTab === 'monitor' && id) {
    const fetchLogs = async () => {
      try {
        setLogsLoading(true)
        const response = await axios.get(`/api/markets/${id}/monitoring-logs`)
        setMonitoringLogs(response.data.logs || [])
      } catch (err) {
        console.error('Failed to fetch monitoring logs:', err)
      } finally {
        setLogsLoading(false)
      }
    }
    
    fetchLogs()
    
    // Only auto-refresh for active markets (not resolved/disputed/cancelled)
    if (market?.status === 'active') {
      const interval = setInterval(fetchLogs, 5000)
      return () => clearInterval(interval)
    }
  }
}, [activeTab, id, market?.status])

// Check if current user is a winner when market resolves
useEffect(() => {
  const checkIfUserWon = async () => {
    if (!publicKey || !market || market.status !== 'resolved') {
      setCheckingWinner(false)
      setUserIsWinner(false)
      setCheckingClaimed(false)
      return
    }
    
    try {
      const response = await axios.get(`/api/markets/${id}/bets`)
      const allBets = response.data.bets || []
      const userBet = allBets.find((bet: any) => 
        bet.wallet_address?.toLowerCase() === publicKey.toString().toLowerCase()
      )
      
      const winningOutcome = market.outcome || market.winningOutcome
      if (userBet && userBet.outcome === winningOutcome) {
        setUserIsWinner(true)
        
        // Check database first
        if (userBet.claimed || userBet.winnings_claimed) {
          console.log('✅ Database shows winnings already claimed')
          setWinningsClaimed(true)
          setCheckingClaimed(false)
          return
        }
        
        // Also check blockchain directly for claimed status
        try {
          // Using connection from useConnection() hook
          const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
          
          const marketIdNum = parseInt(id || '0')
          const marketIdBytes = new Uint8Array(8)
          new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
          
          const [marketPDA] = PublicKey.findProgramAddressSync(
            [Buffer.from("market"), marketIdBytes.slice(0, 6)],
            programId
          )
          
          const [userPositionPDA] = PublicKey.findProgramAddressSync(
            [Buffer.from("position"), marketPDA.toBuffer(), publicKey.toBuffer()],
            programId
          )
          
          // Check if position account exists and has been claimed
          const positionAccount = await connection.getAccountInfo(userPositionPDA)
          if (!positionAccount) {
            // No position account means never bet or already claimed
            console.log('⚠️ No position account found')
            setWinningsClaimed(false)
          } else if (positionAccount.data.length > 0) {
            // Read the position data to check claimed flag
            // Position account structure (from lib.rs):
            // discriminator: 8 bytes (0-7)
            // user: Pubkey 32 bytes (8-39)
            // market: Pubkey 32 bytes (40-71)
            // outcome: u8 1 byte (72)
            // amount: u64 8 bytes (73-80)
            // timestamp: i64 8 bytes (81-88)
            // claimed: bool 1 byte (89) ← THIS IS THE KEY!
            
            const dataView = new DataView(positionAccount.data.buffer, positionAccount.data.byteOffset)
            
            // CORRECT: Check claimed flag at byte 89!
            const claimedFlag = positionAccount.data.length > 89 ? positionAccount.data[89] : 0
            
            console.log('📊 Position account data:', {
              length: positionAccount.data.length,
              claimedFlag: claimedFlag,
              claimedFlagByte89: positionAccount.data[89],
              outcome: positionAccount.data[72],
              amount: Number(dataView.getBigUint64(73, true))
            })
            
            if (claimedFlag === 1) {
              console.log('✅✅✅ BLOCKCHAIN SHOWS ALREADY CLAIMED!')
              setWinningsClaimed(true)
            } else {
              console.log('💰 Winnings ready to claim on blockchain')
              setWinningsClaimed(false)
            }
          } else {
            setWinningsClaimed(false)
          }
        } catch (blockchainError) {
          console.log('Could not check blockchain claim status:', blockchainError)
          // Default to not claimed if we can't check blockchain
          setWinningsClaimed(false)
        }
      } else {
        setUserIsWinner(false)
        setWinningsClaimed(false)
      }
    } catch (error) {
      console.error('Failed to check if user won:', error)
      setUserIsWinner(false)
      setWinningsClaimed(false)
    } finally {
      setCheckingWinner(false)
      setCheckingClaimed(false)
    }
  }
  
  checkIfUserWon()
}, [publicKey, market?.status, market?.outcome, market?.winningOutcome, id])

const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<BetForm>({
defaultValues: {
amount: 100
}
})

const watchedAmount = watch('amount')

// STEP 1: LOAD WIDGET SCRIPTS (API-Sports + TradingView)
useEffect(() => {
// Load API-Sports script
const apiSportsUrl = 'https://widgets.api-sports.io/3.1.0/widgets.js';
const existingApiSports = document.querySelector(`script[src="${apiSportsUrl}"]`);
if (!existingApiSports) {
  console.log('📜 Loading API-Sports script...');
  const apiSportsScript = document.createElement('script');
  apiSportsScript.type = 'module';
  apiSportsScript.src = apiSportsUrl;
  apiSportsScript.onload = () => console.log('✅ API-Sports script loaded');
  apiSportsScript.onerror = (e) => console.error('❌ API-Sports failed:', e);
  document.head.appendChild(apiSportsScript);
}

// Load TradingView script
const tvUrl = 'https://s3.tradingview.com/tv.js';
const existingTv = document.querySelector(`script[src="${tvUrl}"]`);
if (!existingTv) {
  console.log('📜 Loading TradingView script...');
  const tvScript = document.createElement('script');
  tvScript.src = tvUrl;
  tvScript.async = true;
  tvScript.onload = () => {
    console.log('✅ TradingView script loaded');
    setWidgetScriptLoaded(true);
  };
  tvScript.onerror = (e) => console.error('❌ TradingView failed:', e);
  document.head.appendChild(tvScript);
} else {
  setWidgetScriptLoaded(true);
}
}, []);

// STEP 2: PREPARE WIDGET HTML
useEffect(() => {
if (!market?.widget_config) return;
    
try {
const widgetConfig = JSON.parse(market.widget_config);
const apiKey = config.API_FOOTBALL_KEY;
      
// Get HTML and FIX self-closing tags (custom elements need proper closing tags!)
let configHtml = widgetConfig.configCode?.replace('API_KEY', apiKey) || '';
let gameHtml = widgetConfig.widgetCode || '';
      
// Fix self-closing tags: /> → ></api-sports-widget>
configHtml = configHtml.replace(/\s*\/\s*>/, '></api-sports-widget>');
gameHtml = gameHtml.replace(/\s*\/\s*>/, '></api-sports-widget>');
      
const fullHtml = configHtml + gameHtml;
console.log('🚀 Widget HTML prepared (fixed closing tags):', fullHtml.length, 'chars');
console.log('📝 Config:', configHtml);
console.log('🎮 Game:', gameHtml);
setWidgetHtml(fullHtml);
} catch (e) {
console.error('❌ Widget HTML prep failed:', e);
}
}, [market?.widget_config]);

// Don't auto-switch tabs - let user navigate

// STEP 3: INJECT WIDGET WHEN TRACKER TAB IS ACTIVE
useEffect(() => {
if (!market?.widget_config || activeTab !== 'tracker') return;
    
const container = document.getElementById('widget-container-live');
if (!container) {
console.log('⏳ Widget container not in DOM yet');
return;
}
    
// Same 1000ms delay as test page
const timer = setTimeout(() => {
try {
const widgetConfig = JSON.parse(market.widget_config);
        
console.log('🎮 Widget type:', widgetConfig.widgetType);
        
// Handle different widget types
if (widgetConfig.widgetType === 'tradingview') {
  // CRYPTO: TradingView widget
  console.log('💰 Injecting TradingView crypto widget...');
  
  // Clear container
  container.innerHTML = widgetConfig.widgetCode;
  
  // Execute the TradingView widget code
  if ((window as any).TradingView) {
    console.log('✅ TradingView library available, creating widget...');
    const tvSymbol = widgetConfig.needsIds?.tradingViewSymbol || "BINANCE:BTCUSDT";
    console.log(`📊 Creating TradingView widget with symbol: ${tvSymbol}`);
    new (window as any).TradingView.widget({
      container_id: "tradingview_crypto_widget",
      symbol: tvSymbol,
      interval: "1",
      timezone: "Etc/UTC",
      theme: "dark",
      style: "1",
      locale: "en",
      enable_publishing: false,
      hide_top_toolbar: false,
      hide_legend: false,
      save_image: false,
      backgroundColor: "rgba(0, 18, 51, 1)",
      gridColor: "rgba(5, 35, 147, 0.2)",
      allow_symbol_change: false,
      height: "500",
      width: "100%"
    });
    console.log('✅ TradingView widget created!');
  } else {
    console.error('❌ TradingView library not loaded yet');
  }
} else {
  // SPORTS: API-Sports widget
  const apiKey = config.API_FOOTBALL_KEY;
  let configHtml = widgetConfig.configCode?.replace('API_KEY', apiKey) || '';
  let gameHtml = widgetConfig.widgetCode || '';
  
  // Fix self-closing tags
  configHtml = configHtml.replace(/\s*\/\s*>/, '></api-sports-widget>');
  gameHtml = gameHtml.replace(/\s*\/\s*>/, '></api-sports-widget>');
  
  const html = configHtml + gameHtml;
  
  console.log('🚀 Injecting API-Sports widget...');
  console.log('📺 Sport:', widgetConfig.configCode?.match(/data-sport="([^"]+)"/)?.[1] || 'unknown');
  container.innerHTML = html;
  console.log('✅ API-Sports widget injected!');
}
} catch (e) {
console.error('❌ Widget injection failed:', e);
}
}, 1000);
    
return () => clearTimeout(timer);
}, [market, activeTab]);

// Load favorites
useEffect(() => {
  if (publicKey && id) {
    const stored = localStorage.getItem(`favorites_${publicKey.toString()}`)
    if (stored) {
      const favorites = JSON.parse(stored)
      setIsFavorite(favorites.includes(parseInt(id)))
    }
  }
}, [publicKey, id])

// Toggle favorite
const toggleFavorite = () => {
  if (!publicKey) {
    toast.error('Connect wallet to save favorites')
    return
  }
  
  const marketId = parseInt(id || '0')
  const stored = localStorage.getItem(`favorites_${publicKey.toString()}`)
  const favorites = stored ? JSON.parse(stored) : []
  
  const newFavorites = favorites.includes(marketId)
    ? favorites.filter((fid: number) => fid !== marketId)
    : [...favorites, marketId]
  
  setIsFavorite(!isFavorite)
  localStorage.setItem(`favorites_${publicKey.toString()}`, JSON.stringify(newFavorites))
  toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites!')
}

// Share market
const handleShare = async () => {
  const shareData = {
    title: `CPX Market: ${market.question}`,
    text: `Check out this prediction market: ${market.question}`,
    url: window.location.href
  }
  
  try {
    // Try Web Share API first (works on mobile and some desktop browsers)
    if (navigator.share) {
      await navigator.share(shareData)
      toast.success('Shared successfully!')
    } else {
      // Fallback: Copy to clipboard
      await navigator.clipboard.writeText(shareData.url)
      toast.success('Link copied to clipboard!')
    }
  } catch (error: any) {
    // User cancelled or error occurred
    if (error.name !== 'AbortError') {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(shareData.url)
        toast.success('Link copied to clipboard!')
      } catch {
        toast.error('Failed to share')
      }
    }
  }
}

// Admin: Regenerate AI Odds with moderation instructions
const handleRegenerateOdds = async () => {
  if (!agiUser?.isAdmin || !market || !regenerateInstructions.trim()) {
    toast.error('Please enter moderation instructions')
    return
  }
  
  setIsRegeneratingOdds(true)
  
  try {
    // Parse current odds to send as "previousAnalysis"
    let previousAnalysis = null
    try {
      previousAnalysis = JSON.parse(market.agi_odds_reasoning || '{}')
    } catch {
      previousAnalysis = { reasoning: market.agi_odds_reasoning }
    }
    
    agiApi.defaults.headers.common['Authorization'] = `Bearer ${agiToken}`
    const response = await agiApi.post(`/api/agi/admin/markets/${market.id}/regenerate-odds`, {
      customInstructions: regenerateInstructions.trim(),
      previousAnalysis
    })
    
    if (response.data.success) {
      toast.success('AI odds regenerated! 🎲')
      // Update market with new analysis
      setMarket({
        ...market,
        agi_odds_reasoning: JSON.stringify(response.data.newAnalysis)
      })
      setRegenerateInstructions('')
    }
  } catch (error: any) {
    console.error('Regenerate odds error:', error)
    toast.error(error.response?.data?.error || 'Failed to regenerate odds')
  } finally {
    setIsRegeneratingOdds(false)
  }
}

// Load market data - FETCH FROM BLOCKCHAIN!
useEffect(() => {
const loadMarket = async () => {
try {
setLoading(true)
// 1. First get from database
const response = await axios.get(`/api/markets/${id}`)
setMarket(response.data)
        
// Check if market is resolved
if (response.data.status === 'resolved') {
console.log('✅ Market is RESOLVED - showing final state');
}
        
// 2. Then fetch REAL data from blockchain - THE SOURCE OF TRUTH!
try {
// Using connection from useConnection() hook
const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
          
// Derive market PDA
const marketIdNum = parseInt(id || '0')
const marketIdBytes = new Uint8Array(8)
new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
const [marketPDA] = PublicKey.findProgramAddressSync(
[Buffer.from("market"), marketIdBytes.slice(0, 6)],
programId
)
          
console.log('🔍 Fetching market from blockchain:', marketPDA.toString())
          
// Fetch the market account data from blockchain
const marketAccount = await connection.getAccountInfo(marketPDA)
if (marketAccount && marketAccount.data) {
// Parse the market data 
// The data layout in TestCase struct:
// discriminator: [u8; 8] (0-7)
// test_id: u64 (8-15)
// authority: Pubkey (16-47)
// config: Pubkey (48-79)
// question: String (80+)
// After variable length string, we have more fields
            
const dataView = new DataView(marketAccount.data.buffer, marketAccount.data.byteOffset)
            
// Read question string length first to know where other fields are
const questionLenOffset = 80
const questionLen = dataView.getUint32(questionLenOffset, true)
            
// After question string, the layout continues:
// closes_at_unix: i64
// resolved: bool  
// winning_outcome: u8
// total_yes: u64
// total_no: u64
            
const afterQuestionOffset = questionLenOffset + 4 + questionLen
const totalYesOffset = afterQuestionOffset + 8 + 1 + 1 // After closes_at, resolved, winning_outcome
const totalNoOffset = totalYesOffset + 8
            
const totalYesOnChain = Number(dataView.getBigUint64(totalYesOffset, true))
const totalNoOnChain = Number(dataView.getBigUint64(totalNoOffset, true))
            
console.log('💰 BLOCKCHAIN POOL DATA:', {
marketPDA: marketPDA.toString(),
totalYes: totalYesOnChain,
totalNo: totalNoOnChain,
totalPool: totalYesOnChain + totalNoOnChain
})
            
// UPDATE WITH REAL BLOCKCHAIN DATA!
setMarket(prev => ({
...prev!,
totalYes: totalYesOnChain,
totalNo: totalNoOnChain,
total_yes: totalYesOnChain,
total_no: totalNoOnChain
}))
            
// Sync database to match blockchain
if ((totalYesOnChain > 0 || totalNoOnChain > 0) && 
(response.data.total_yes !== totalYesOnChain || response.data.total_no !== totalNoOnChain)) {
console.log('📝 Syncing database to match blockchain...')
axios.patch(`/api/markets/${id}`, {
total_yes_bets: totalYesOnChain,
total_no_bets: totalNoOnChain,
total_volume: totalYesOnChain + totalNoOnChain
}).then(() => {
console.log('✅ Database synced with blockchain!')
}).catch(err => {
console.log('Could not sync database:', err)
})
}
} else {
console.log('Market not found on blockchain yet')
}
} catch (blockchainError) {
console.log('Using database values (blockchain fetch failed):', blockchainError)
}
} catch (error) {
console.error('Failed to load market:', error)
toast.error('Failed to load market')
navigate('/markets')
} finally {
setLoading(false)
}
}

if (id) {
loadMarket()
      
// Poll for updates every 5 seconds (status, odds, pools)
const pollInterval = setInterval(async () => {
try {
const response = await axios.get(`/api/markets/${id}`)
const newStatus = response.data.status
const oldStatus = market?.status

// Always update market data (for odds, pools, etc.)
setMarket(response.data)
          
if (newStatus !== oldStatus) {
console.log(`🔄 Market status changed: ${oldStatus} → ${newStatus}`)
            
if (newStatus === 'resolved' || newStatus === 'disputed') {
console.log(newStatus === 'resolved' ? '🎉 MARKET JUST RESOLVED!' : '⚠️ MARKET WENT TO DISPUTE!')
clearInterval(pollInterval) // Stop polling once done
              
// Fetch evidence and auto-show resolution modal
axios.get(`/api/oracle/markets/${id}/evidence`).then(evidenceRes => {
console.log('📊 Evidence loaded:', evidenceRes.data)
setResolutionEvidence(evidenceRes.data.resolution?.evidence || evidenceRes.data.evidence || [])
setShowResolutionModal(true) // Auto-show modal
}).catch(err => {
console.log('Could not load evidence:', err)
setShowResolutionModal(true) // Show modal anyway
})
}
}
} catch (error) {
console.error('Polling error:', error)
}
}, 5000) // Poll every 5 seconds
      
// Cleanup on unmount
return () => clearInterval(pollInterval)
}
}, [id, navigate, market?.status])

// Auto-load validation plan data
useEffect(() => {
  const loadValidationPlan = async () => {
    try {
      const response = await axios.get(`/api/oracle/markets/${id}/evidence`)
      console.log('🔍 VALIDATION PLAN DATA:', response.data)
      console.log('🔍 creation:', response.data.creation)
      console.log('🔍 validations:', response.data.creation?.validations)
      console.log('🔍 validations[0]:', response.data.creation?.validations?.[0])
      console.log('🔍 validation_plan:', response.data.creation?.validations?.[0]?.validation_plan)
      
      if (response.data.creation?.validations?.[0]) {
        setValidationPlan(response.data.creation)
      } else if (response.data.creation) {
        setValidationPlan(response.data.creation)
      } else {
        setValidationPlan(response.data)
      }
    } catch (error) {
      console.log('Could not load validation plan:', error)
    }
  }
  
  if (id) {
    loadValidationPlan()
  }
}, [id])

// Calculate odds with proper updates
const yesAmount = market?.totalYes ?? market?.total_yes ?? 0
const noAmount = market?.totalNo ?? market?.total_no ?? 0
const totalPool = yesAmount + noAmount
const yesOdds = totalPool > 0 ? (yesAmount / totalPool * 100).toFixed(1) : '50.0'
const noOdds = totalPool > 0 ? (noAmount / totalPool * 100).toFixed(1) : '50.0'

// Place bet
const onSubmit = async (data: BetForm) => {
if (!connected || !publicKey) {
toast.error('Please connect your wallet')
return
}

if (selectedOutcome === null) {
toast.error('Please select YES or NO')
return
}

try {
setBetting(true)
      
// 1. First place bet on blockchain
// Using connection from useConnection() hook
const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
const tokenMint = new PublicKey('5mbkMp8x4aZ9ntsVPPnVUFekiE1joGU4gSANfsTtiiaT')
      
// Get market's market_id from backend or derive from the ID
const marketIdNum = parseInt(id || '0')
const marketIdBytes = new Uint8Array(8)
new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
      
// Derive PDAs
const [configPDA] = PublicKey.findProgramAddressSync(
[Buffer.from("config")],
programId
)
      
const [marketPDA] = PublicKey.findProgramAddressSync(
[Buffer.from("market"), marketIdBytes.slice(0, 6)],
programId
)
      
const [userPositionPDA] = PublicKey.findProgramAddressSync(
[Buffer.from("position"), marketPDA.toBuffer(), publicKey.toBuffer()],
programId
)
      
const [yesVaultAuth] = PublicKey.findProgramAddressSync(
[Buffer.from("yes_vault_auth"), marketPDA.toBuffer()],
programId
)
      
const [noVaultAuth] = PublicKey.findProgramAddressSync(
[Buffer.from("no_vault_auth"), marketPDA.toBuffer()],
programId
)
      
const yesVault = await getAssociatedTokenAddress(tokenMint, yesVaultAuth, true)
const noVault = await getAssociatedTokenAddress(tokenMint, noVaultAuth, true)
const userTokenAccount = await getAssociatedTokenAddress(tokenMint, publicKey)
      
// Check if user token account exists, create if not
const userTokenAccountInfo = await connection.getAccountInfo(userTokenAccount);
if (!userTokenAccountInfo) {
console.log('🔧 Creating token account for betting...');
const createAtaIx = createAssociatedTokenAccountInstruction(
publicKey,
userTokenAccount,
publicKey,
tokenMint
);
const createAtaTx = new Transaction().add(createAtaIx);
const { blockhash } = await connection.getLatestBlockhash();
createAtaTx.recentBlockhash = blockhash;
createAtaTx.feePayer = publicKey;
        
const signedAtaTx = await (window as any).solana.signTransaction(createAtaTx);
const ataSig = await connection.sendRawTransaction(signedAtaTx.serialize());
await connection.confirmTransaction(ataSig, 'confirmed');
console.log('✅ Token account created for betting!');
toast.success('Token account initialized!');
}
      
// Build stake_tokens instruction
const discriminator = Buffer.from([136, 126, 91, 162, 40, 131, 13, 127]) // stake_tokens discriminator - CORRECTED!
const args = Buffer.concat([
discriminator,
Buffer.from([selectedOutcome]), // outcome (YES=1, NO=0)
Buffer.from(new Uint8Array(new BigUint64Array([BigInt(data.amount)]).buffer)) // amount
])
      
const accounts = [
{ pubkey: configPDA, isSigner: false, isWritable: true },
{ pubkey: marketPDA, isSigner: false, isWritable: true },
{ pubkey: yesVaultAuth, isSigner: false, isWritable: false },
{ pubkey: noVaultAuth, isSigner: false, isWritable: false },
{ pubkey: yesVault, isSigner: false, isWritable: true },
{ pubkey: noVault, isSigner: false, isWritable: true },
{ pubkey: tokenMint, isSigner: false, isWritable: false },
{ pubkey: publicKey, isSigner: true, isWritable: true },
{ pubkey: userPositionPDA, isSigner: false, isWritable: true },
{ pubkey: userTokenAccount, isSigner: false, isWritable: true },
{ pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
{ pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
{ pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
{ pubkey: new PublicKey("SysvarRent111111111111111111111111111111111"), isSigner: false, isWritable: false }
]
      
const stakeInstruction = new TransactionInstruction({
keys: accounts,
programId,
data: args
})
      
// Add a unique memo to make each transaction different
const memoInstruction = new TransactionInstruction({
  keys: [],
  programId: new PublicKey('MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr'),
  data: Buffer.from(`Bet ${Date.now()}-${Math.random().toString(36).substring(7)}`, 'utf-8')
})

const transaction = new Transaction()
  .add(memoInstruction)  // Add memo first to make transaction unique
  .add(stakeInstruction)
transaction.feePayer = publicKey
const { blockhash } = await connection.getLatestBlockhash()
transaction.recentBlockhash = blockhash
      
console.log('🎲 Placing bet:', data.amount, 'tokens on', selectedOutcome === 1 ? 'YES' : 'NO')
      
const signedTx = await (window as any).solana.signTransaction(transaction)
const signature = await connection.sendRawTransaction(signedTx.serialize())
      
console.log('📡 Bet transaction sent:', signature)
console.log('⏳ Waiting for confirmation (may take up to 30 seconds on devnet)...')
console.log('🔗 CHECK EXPLORER:', `https://explorer.solana.com/tx/${signature}?cluster=devnet`)
      
// Use polling instead of WebSocket for more reliable confirmation on devnet
let confirmed = false
let attempts = 0
const maxAttempts = 60 // 60 attempts * 1 second = 60 seconds max
      
while (!confirmed && attempts < maxAttempts) {
  attempts++
  try {
    const status = await connection.getSignatureStatus(signature)
    console.log(`⏳ Attempt ${attempts}: Status =`, status?.value?.confirmationStatus)
    
    if (status?.value?.confirmationStatus === 'confirmed' || status?.value?.confirmationStatus === 'finalized') {
      confirmed = true
      console.log('✅ Bet confirmed on blockchain!')
      break
    }
    
    if (status?.value?.err) {
      console.error('❌ BET TRANSACTION FAILED:', status.value.err)
      throw new Error(`Blockchain bet failed: ${JSON.stringify(status.value.err)}`)
    }
  } catch (e) {
    console.log(`⚠️ Attempt ${attempts} failed to get status:`, e)
  }
  
  // Wait 1 second before next attempt
  await new Promise(resolve => setTimeout(resolve, 1000))
}
      
if (!confirmed) {
  // Transaction might still have succeeded, check explorer
  console.warn('⚠️ Confirmation timeout, but bet may have succeeded!')
  console.log('🔗 CHECK EXPLORER:', `https://explorer.solana.com/tx/${signature}?cluster=devnet`)
  
  // One last check with finalized commitment
  const finalStatus = await connection.getSignatureStatus(signature)
  if (finalStatus?.value && !finalStatus.value.err) {
    console.log('✅ Bet found in final check!')
    confirmed = true
  } else {
    throw new Error('Bet confirmation timeout - check Solana Explorer to verify')
  }
}
      
console.log('✅ Bet placed on blockchain:', signature)
toast.success('Bet placed on blockchain!')
      
// 2. Fetch updated pool data from blockchain
console.log('📊 Fetching updated pool data from blockchain...')
      
// Re-fetch the market account data from blockchain
const marketAccount = await connection.getAccountInfo(marketPDA)
if (marketAccount && marketAccount.data) {
const dataView = new DataView(marketAccount.data.buffer, marketAccount.data.byteOffset)
        
// Read question string length to find other fields
const questionLenOffset = 80
const questionLen = dataView.getUint32(questionLenOffset, true)
        
// After question string: closes_at, resolved, winning_outcome, total_yes, total_no
const afterQuestionOffset = questionLenOffset + 4 + questionLen
const totalYesOffset = afterQuestionOffset + 8 + 1 + 1
const totalNoOffset = totalYesOffset + 8
        
const totalYesOnChain = Number(dataView.getBigUint64(totalYesOffset, true))
const totalNoOnChain = Number(dataView.getBigUint64(totalNoOffset, true))
        
console.log('💰 UPDATED BLOCKCHAIN POOLS:', {
totalYes: totalYesOnChain,
totalNo: totalNoOnChain,
totalPool: totalYesOnChain + totalNoOnChain,
yesPercent: totalYesOnChain + totalNoOnChain > 0 ? (totalYesOnChain / (totalYesOnChain + totalNoOnChain) * 100).toFixed(1) : '50.0',
noPercent: totalYesOnChain + totalNoOnChain > 0 ? (totalNoOnChain / (totalYesOnChain + totalNoOnChain) * 100).toFixed(1) : '50.0'
})
        
// UPDATE UI WITH NEW BLOCKCHAIN DATA!
setMarket(prev => ({
...prev!,
totalYes: totalYesOnChain,
totalNo: totalNoOnChain,
total_yes: totalYesOnChain,
total_no: totalNoOnChain
}))
        
// Reset the betting form
setSelectedOutcome(null)
setValue('amount', 100)
}
      
// 3. Update backend for consistency
const payload = {
walletAddress: publicKey.toString(),
outcome: selectedOutcome,
amount: data.amount,
signature
}
      
try {
await axios.post(`/api/markets/${id}/bet`, payload)
} catch (err) {
console.log('Backend update failed, but bet is on blockchain:', err)
}
      
} catch (error: any) {
console.error('Bet placement error:', error)
      
// Check for specific blockchain errors
let errorMessage = 'Failed to place bet'
      
if (error.message?.includes('CantSwitchSides') || error.message?.includes('0x1783')) {
errorMessage = `You've already bet on ${selectedOutcome === 1 ? 'NO' : 'YES'} with this wallet. You can only add more bets to the same side, not switch sides. Use a different wallet to bet on the opposite outcome.`
} else if (error.message?.includes('MarketNotApproved')) {
errorMessage = 'This market is not yet approved for betting. Please wait for AI approval.'
} else if (error.message?.includes('InsufficientFunds')) {
errorMessage = 'Insufficient token balance. Please ensure you have enough tokens.'
} else if (error.response?.data?.error) {
errorMessage = error.response.data.error
} else if (error.message) {
errorMessage = error.message
}
      
toast.error(errorMessage, { duration: 6000 })
} finally {
setBetting(false)
}
}

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-300">Loading market...</p>
        </div>
      </div>
    )
  }

  if (!market) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Market Not Found</h2>
          <button onClick={() => navigate('/markets')} className="btn-primary">
            Back to Markets
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/markets')}
            className="btn-secondary flex items-center space-x-2 mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Markets</span>
          </button>

          <div className="card p-6 border-l-4 border-[#2AAAEA]">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1 min-w-0">
                <h1 className="text-3xl font-black text-white mb-2 break-words">{market.question}</h1>
                <div className="flex items-center space-x-4 text-sm text-gray-400 flex-wrap gap-y-1">
<span className="px-2 py-1 rounded text-xs font-bold bg-[#052393]/40 text-cpx-light-blue uppercase tracking-wider">{market.category === 'agi_manual' ? '✨ AGI' : market.category}</span>
{!(market.is_manual === 1 || market.category === 'agi_manual') && (
  <span>💰 Total Pool: {totalPool?.toLocaleString() || 0} tokens</span>
)}
<span>👤 Creator: {market.creator_name || market.creator_email || (market.creator_address?.startsWith('agi_email_') ? 'AGI User' : market.creator_address) || market.creator || 'Unknown'}</span>
</div>
</div>
<div className="flex items-center space-x-3">
<div className={`px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${
market.status === 'active' ? 'bg-[#009245]/20 text-cpx-green' : 
market.status === 'closed' ? 'bg-[#FBB03B]/20 text-cpx-yellow' :
market.status === 'disputed' ? 'bg-red-500/20 text-red-400' : 'bg-gray-700 text-gray-400'
}`}>
{market.status === 'disputed' ? '⚠️ DISPUTED' : market.status}
</div>
<button
  onClick={handleShare}
  className="p-2 rounded-lg transition-all text-gray-500 hover:text-cpx-light-blue hover:bg-[#2AAAEA]/10"
  title="Share market"
>
  <Share2 className="w-5 h-5" />
</button>
<button
  onClick={toggleFavorite}
  className={`p-2 rounded-lg transition-all ${
    isFavorite
      ? 'text-cpx-yellow bg-[#FBB03B]/20 hover:bg-[#FBB03B]/30'
      : 'text-gray-500 hover:text-cpx-yellow hover:bg-[#FBB03B]/10'
  }`}
  title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
>
  <Star className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
</button>
</div>
</div>

{market.description && (
<p className="text-gray-300 mb-4 break-words whitespace-pre-wrap">{market.description}</p>
)}

<div className="flex items-center justify-between text-sm text-gray-200">
<span>📅 Closes: {market.betting_ends_at || market.closes_at || market.closesAt ? new Date(market.betting_ends_at || market.closes_at || market.closesAt).toLocaleDateString() : 'No end date'}</span>
<span>🕐 Created: {market.created_at || market.createdAt ? new Date(market.created_at || market.createdAt).toLocaleDateString() : 'Unknown'}</span>
</div>
</div>
</div>

{/* Tab Navigation */}
<div className="mb-6">
<div className="flex items-center space-x-1 border-b border-gray-800/50">
{market?.widget_config && (
<button
onClick={() => setActiveTab('tracker')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'tracker'
? 'text-white'
: 'text-gray-400 hover:text-gray-200'
}`}
>
<Tv className="w-4 h-4" />
<span>Live Tracker</span>
{activeTab === 'tracker' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
)}
{(market?.widget_config || market?.category === 'crypto' || market?.category === 'sports') && market?.status === 'active' && (
<button
onClick={() => setActiveTab('monitor')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'monitor'
? 'text-white'
: 'text-gray-400 hover:text-gray-200'
}`}
>
<Radio className="w-4 h-4" />
<span>Event Monitor</span>
{activeTab === 'monitor' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
)}
<button
onClick={() => setActiveTab('odds')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'odds'
? 'text-white'
: 'text-gray-400 hover:text-gray-200'
}`}
>
<Activity className="w-4 h-4" />
<span>Odds</span>
{activeTab === 'odds' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
<button
onClick={() => setActiveTab('chat')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'chat'
? 'text-white'
: 'text-gray-400 hover:text-gray-200'
}`}
>
<MessageCircle className="w-4 h-4" />
<span>Chat</span>
{activeTab === 'chat' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
<button
onClick={() => setActiveTab('stats')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'stats'
? 'text-white'
: 'text-gray-400 hover:text-gray-200'
}`}
>
<BarChart3 className="w-4 h-4" />
<span>Stats</span>
{activeTab === 'stats' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
{/* Plan tab - show different label for AGI markets */}
<button
onClick={() => setActiveTab('plan')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'plan'
? 'text-white'
: 'text-gray-400 hover:text-gray-200'
}`}
>
<FileText className="w-4 h-4" />
<span>{(market.is_manual === 1 || market.category === 'agi_manual') ? 'Rules' : 'Plan'}</span>
{activeTab === 'plan' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#2AAAEA] to-[#FBB03B]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
{/* AI Insight tab - Only for AGI Markets */}
{(market.is_manual === 1 || market.category === 'agi_manual') && market.agi_odds_reasoning && (
<button
onClick={() => setActiveTab('ai_insight')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'ai_insight'
? 'text-white'
: 'text-yellow-400 hover:text-yellow-300'
}`}
>
<Sparkles className="w-4 h-4" />
<span>AI Insight</span>
{activeTab === 'ai_insight' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-yellow-400 to-orange-500"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
)}
{(market.source_article_url || market.source_article_data) && (
<button
onClick={() => setActiveTab('article')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'article'
? 'text-white'
: 'text-gray-400 hover:text-gray-200'
}`}
>
<FileText className="w-4 h-4" />
<span>📰 Article</span>
{activeTab === 'article' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#FBB03B] to-[#FF8C00]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
)}
{(market.status === 'resolved' || market.status === 'disputed') && (
<button
onClick={() => setActiveTab('resolution')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'resolution'
? 'text-white'
: 'text-emerald-400 hover:text-emerald-300'
}`}
>
<CheckCircle className="w-4 h-4" />
<span>Resolution</span>
{activeTab === 'resolution' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-emerald-400 to-emerald-600"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
          )}
          {/* Smart Tab: Only show if user won */}
          {connected && userIsWinner && !checkingWinner && market.status === 'resolved' && (
<button
onClick={() => setActiveTab('winnings')}
className={`px-4 py-3 font-semibold transition-all duration-200 relative flex items-center space-x-2 ${
activeTab === 'winnings'
? 'text-white'
: 'text-cpx-green hover:text-white'
}`}
>
<Award className="w-4 h-4" />
<span>{winningsClaimed ? 'Claimed' : 'My Winnings'}</span>
{activeTab === 'winnings' && (
<motion.div
layoutId="tab-indicator"
className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#009245] to-[#FBB03B]"
initial={false}
transition={{ type: "spring", stiffness: 300, damping: 30 }}
/>
)}
</button>
          )}
        </div>
        </div>

        {/* Tab Content - FULL WIDTH ONLY */}
        <div>
            
          {/* TAB: Live Tracker */}
          {activeTab === 'tracker' && market?.widget_config && (
            <div className="card p-6 mb-6 border-l-2 border-purple-500">
          <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
          <span className="text-2xl mr-2">📺</span>
          Live Event Tracker
          </h2>
          <div 
          id="widget-container-live"
          className="bg-gray-900/30 p-4 rounded-lg border border-gray-700 min-h-[500px]"
          />
          </div>
          )}
          
          {/* TAB: Event Monitor */}
          {activeTab === 'monitor' && (market?.widget_config || market?.category === 'crypto' || market?.category === 'sports') && (
            <div className="card p-6 mb-6 border-l-2 border-[#2AAAEA]">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white flex items-center">
                  <Radio className="w-6 h-6 mr-2 text-cpx-light-blue" />
                  Event Monitor
                </h2>
                <div className="flex items-center space-x-2 text-sm text-gray-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Live monitoring active</span>
                </div>
              </div>
              
              {logsLoading && monitoringLogs.length === 0 ? (
                <div className="text-center py-8">
                  <Loader className="w-8 h-8 animate-spin text-cpx-light-blue mx-auto mb-2" />
                  <p className="text-gray-400 text-sm">Loading monitoring logs...</p>
                </div>
              ) : monitoringLogs.length === 0 ? (
                <div className="text-center py-8 bg-gray-900/30 rounded-lg border border-gray-700">
                  <Radio className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-400">No monitoring activity yet</p>
                  <p className="text-gray-500 text-sm mt-1">Event monitoring will appear here when it starts</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {monitoringLogs.map((log, idx) => (
                    <div 
                      key={log.id || idx}
                      className={`p-3 rounded-lg border ${
                        log.log_type === 'checking' ? 'bg-blue-900/20 border-blue-500/30' :
                        log.log_type === 'not_met' ? 'bg-gray-900/30 border-gray-700' :
                        log.log_type === 'found' ? 'bg-green-900/20 border-green-500/30' :
                        log.log_type === 'error' ? 'bg-red-900/20 border-red-500/30' :
                        'bg-gray-900/20 border-gray-700'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            {log.log_type === 'checking' && <Activity className="w-3 h-3 text-blue-400 animate-pulse" />}
                            {log.log_type === 'not_met' && <Clock className="w-3 h-3 text-gray-400" />}
                            {log.log_type === 'found' && <CheckCircle className="w-3 h-3 text-green-400" />}
                            {log.log_type === 'error' && <XCircle className="w-3 h-3 text-red-400" />}
                            <span className="text-xs text-gray-400">
                              {new Date(log.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-200">{log.message}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
                      
          {/* TAB: Odds & Chart */}
          {activeTab === 'odds' && (
          <div className="space-y-6">
          
          {/* AGI Credit Betting Interface - For AGI Markets */}
          {(market.is_manual === 1 || market.category === 'agi_manual') && (
            <AgiCreditBetting
              marketId={market.id}
              question={market.question}
              totalYes={market.total_yes_bets || market.totalYes || 0}
              totalNo={market.total_no_bets || market.totalNo || 0}
              status={market.status}
              bettingEndsAt={market.betting_ends_at || market.closesAt || ''}
              isAdmin={agiUser?.isAdmin}
              onBetPlaced={async () => {
                // Refetch market data instead of full page reload
                try {
                  const response = await axios.get(`/api/markets/${id}`)
                  setMarket(response.data)
                } catch (e) {
                  console.error('Failed to refresh market:', e)
                }
              }}
              onResolved={async () => {
                // Refetch market data instead of full page reload
                try {
                  const response = await axios.get(`/api/markets/${id}`)
                  setMarket(response.data)
                } catch (e) {
                  console.error('Failed to refresh market:', e)
                }
              }}
            />
          )}
          
          {/* AGI Market Current Odds - Visual Display with Credits */}
          {(market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="card p-6 mb-6 border-yellow-500/30">
            <h2 className="text-xl font-black text-white mb-6 flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-yellow-400" />
              Current Odds
            </h2>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6 text-center">
                <div className="text-4xl font-black text-emerald-400 transition-all duration-500">
                  {(() => {
                    const yes = market.total_yes_bets || 0;
                    const no = market.total_no_bets || 0;
                    const total = yes + no;
                    return total > 0 ? Math.round((yes / total) * 100) : 50;
                  })()}%
                </div>
                <div className="text-sm text-white font-bold uppercase tracking-wider mt-2">YES</div>
                <div className="text-xs text-gray-400 mt-1">
                  Pool: {(market.total_yes_bets || 0).toLocaleString()} credits
                </div>
              </div>
              
              <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-6 text-center">
                <div className="text-4xl font-black text-rose-400 transition-all duration-500">
                  {(() => {
                    const yes = market.total_yes_bets || 0;
                    const no = market.total_no_bets || 0;
                    const total = yes + no;
                    return total > 0 ? Math.round((no / total) * 100) : 50;
                  })()}%
                </div>
                <div className="text-sm text-white font-bold uppercase tracking-wider mt-2">NO</div>
                <div className="text-xs text-gray-400 mt-1">
                  Pool: {(market.total_no_bets || 0).toLocaleString()} credits
                </div>
              </div>
            </div>
            
            {/* Visual Progress Bar */}
            <div className="w-full bg-gray-800 rounded-full h-3 mb-2 overflow-hidden">
              <div 
                className="h-3 rounded-full transition-all duration-500 ease-out"
                style={{
                  background: `linear-gradient(to right, #10b981 0%, #10b981 ${(() => {
                    const yes = market.total_yes_bets || 0;
                    const no = market.total_no_bets || 0;
                    const total = yes + no;
                    return total > 0 ? Math.round((yes / total) * 100) : 50;
                  })()}%, #f43f5e ${(() => {
                    const yes = market.total_yes_bets || 0;
                    const no = market.total_no_bets || 0;
                    const total = yes + no;
                    return total > 0 ? Math.round((yes / total) * 100) : 50;
                  })()}%, #f43f5e 100%)`
                }}
              ></div>
            </div>
            <div className="flex justify-between text-xs font-bold text-gray-300">
              <span className="text-emerald-400">YES {(() => {
                const yes = market.total_yes_bets || 0;
                const no = market.total_no_bets || 0;
                const total = yes + no;
                return total > 0 ? Math.round((yes / total) * 100) : 50;
              })()}%</span>
              <span className="text-rose-400">NO {(() => {
                const yes = market.total_yes_bets || 0;
                const no = market.total_no_bets || 0;
                const total = yes + no;
                return total > 0 ? Math.round((no / total) * 100) : 50;
              })()}%</span>
            </div>
            
            {/* Total Pool Info */}
            <div className="mt-4 pt-4 border-t border-gray-700 text-center">
              <span className="text-gray-400">Total Pool: </span>
              <span className="text-yellow-400 font-bold">
                {((market.total_yes_bets || 0) + (market.total_no_bets || 0)).toLocaleString()} credits
              </span>
            </div>
          </div>
          )}
          
          {/* Regular Blockchain Betting Interface - At Top (NOT for AGI markets) */}
          {!(market.is_manual === 1 || market.category === 'agi_manual') && (
          <>
          {connected && market.status === 'active' && new Date(market.betting_ends_at) > new Date() ? (
          <div className="card p-6 bg-gradient-to-r from-blue-900/20 to-purple-900/20 border-2 border-cyan-500">
          <h3 className="text-lg font-semibold text-white mb-6 flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-cyan-400" />
          Place Your Bet
          </h3>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Outcome Selection */}
          <div>
          <label className="block text-sm font-medium text-gray-300 mb-3">
          Your Prediction *
          </label>
          <div className="grid grid-cols-2 gap-3">
          <button
          type="button"
          onClick={() => setSelectedOutcome(1)}
          className={`p-4 rounded-lg border-2 text-center font-semibold transition-all ${
          selectedOutcome === 1
          ? 'border-green-500 bg-green-50 text-green-700'
          : 'border-gray-700 hover:border-green-300 text-gray-300'
          }`}
          >
          <div className="text-lg">YES</div>
          <div className="text-xs">{yesPercent}% chance</div>
          </button>
          <button
          type="button"
          onClick={() => setSelectedOutcome(0)}
          className={`p-4 rounded-lg border-2 text-center font-semibold transition-all ${
          selectedOutcome === 0
          ? 'border-red-500 bg-red-50 text-red-700'
          : 'border-gray-700 hover:border-red-300 text-gray-300'
          }`}
          >
          <div className="text-lg">NO</div>
          <div className="text-xs">{noPercent}% chance</div>
          </button>
          </div>
          </div>

          {/* Bet Amount */}
          <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
          Bet Amount (Your Tokens) *
          </label>
          <input
          {...register('amount', { 
          required: 'Bet amount is required',
          min: { value: 1, message: 'Minimum bet is 1 token' },
          max: { value: 100000, message: 'Maximum bet is 100,000 tokens' }
          })}
          type="number"
          className={`input ${errors.amount ? 'border-red-300' : ''}`}
          min="1"
          step="1"
          placeholder="100"
          />
          {errors.amount && (
          <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>
          )}
          <div className="mt-2 text-xs text-gray-200">
          💡 Using your custom tokens: 5mbk...tiiaT
          </div>
          </div>

          {/* Submit Button */}
          <button
          type="submit"
          className={`w-full btn-primary flex items-center justify-center space-x-2 ${
          selectedOutcome === 1 ? 'bg-green-600 hover:bg-green-700' :
          selectedOutcome === 0 ? 'bg-red-600 hover:bg-red-700' : ''
          }`}
          disabled={betting || selectedOutcome === null}
          >
          {betting ? (
          <>
          <Loader className="w-4 h-4 animate-spin" />
          <span>Placing Bet...</span>
          </>
          ) : (
          <>
          <Target className="w-4 h-4" />
          <span>
          Bet {watchedAmount} tokens on {selectedOutcome === 1 ? 'YES' : selectedOutcome === 0 ? 'NO' : '...'}
          </span>
          </>
          )}
          </button>
          </form>
          </div>
          ) : !connected ? (
          <div className="card p-6 text-center border border-gray-800 bg-gray-900/50">
          <Brain className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="font-semibold text-white mb-2">Connect Wallet to Bet</h3>
          <p className="text-gray-400 text-sm mb-4">
          Connect your wallet to place bets on this market
          </p>
          </div>
          ) : (
          <div className="card p-6 text-center bg-gray-900/50">
          <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="font-semibold text-white mb-2">Betting Closed</h3>
          <p className="text-gray-300 text-sm">
          This market is no longer accepting bets
          </p>
          </div>
          )}
          </>
          )}
                          
          {/* SECTION 1: Current Odds - Show this FIRST (NOT for AGI markets) */}
          {!(market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="card p-6 mb-6  border-[#052393]/40">
          <h2 className="text-xl font-black text-white mb-6 flex items-center justify-between">
          <span className="flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-cpx-light-blue" />
          Current Odds <span className="text-xs ml-2 text-yellow-400 animate-pulse">LIVE</span>
          {/* AI Initial Odds Info Button */}
          {market?.ai_odds_yes && market?.ai_odds_no && (
            <button
              onClick={() => setShowOddsReasoningModal(true)}
              className="ml-2 text-gray-400 hover:text-[#2AAAEA] transition-colors"
              title="View Initial AI Odds Analysis"
            >
              <Info className="w-4 h-4" />
            </button>
          )}
          </span>
          <button
          onClick={async () => {
          try {
          // Using connection from useConnection() hook
          const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
          const marketIdNum = parseInt(id || '0')
          const marketIdBytes = new Uint8Array(8)
          new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
          const [marketPDA] = PublicKey.findProgramAddressSync(
          [Buffer.from("market"), marketIdBytes.slice(0, 6)],
          programId
          )
                                
          const marketAccount = await connection.getAccountInfo(marketPDA)
          if (marketAccount && marketAccount.data) {
          const dataView = new DataView(marketAccount.data.buffer, marketAccount.data.byteOffset)
          const questionLenOffset = 80
          const questionLen = dataView.getUint32(questionLenOffset, true)
          const afterQuestionOffset = questionLenOffset + 4 + questionLen
          const totalYesOffset = afterQuestionOffset + 8 + 1 + 1
          const totalNoOffset = totalYesOffset + 8
                                  
          const totalYesOnChain = Number(dataView.getBigUint64(totalYesOffset, true))
          const totalNoOnChain = Number(dataView.getBigUint64(totalNoOffset, true))
                                  
          setMarket(prev => ({
          ...prev!,
          totalYes: totalYesOnChain,
          totalNo: totalNoOnChain,
          total_yes: totalYesOnChain,
          total_no: totalNoOnChain
          }))
                                  
          toast.success('Refreshed from blockchain!')
          }
          } catch (error) {
          console.error('Refresh error:', error)
          toast.error('Failed to refresh')
          }
          }}
          className="text-sm text-cpx-light-blue hover:text-glow-aqua flex items-center space-x-1"
          title="Refresh from blockchain"
          >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
          </button>
          </h2>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-[#009245]/10 border border-[#009245]/30 rounded-lg p-6 text-center">
          <div className="text-4xl font-black text-cpx-green transition-all duration-500">{yesPercent}%</div>
          <div className="text-sm text-white font-bold uppercase tracking-wider mt-2">YES</div>
          <div className="text-xs text-gray-400 mt-1">
          Pool: {yesAmount.toLocaleString()} tokens
          </div>
          {priceDirection.yes === 'up' && (
            <div className="text-xs text-green-400 mt-1 animate-pulse">↑ Rising</div>
          )}
          {priceDirection.yes === 'down' && (
            <div className="text-xs text-green-600 mt-1 animate-pulse">↓ Falling</div>
          )}
          </div>
                          
          <div className="bg-red-900/10 border border-red-500/30 rounded-lg p-6 text-center">
          <div className="text-4xl font-black text-red-400 transition-all duration-500">{noPercent}%</div>
          <div className="text-sm text-white font-bold uppercase tracking-wider mt-2">NO</div>
          <div className="text-xs text-gray-400 mt-1">
          Pool: {noAmount.toLocaleString()} tokens
          </div>
          {priceDirection.no === 'down' && (
            <div className="text-xs text-red-600 mt-1 animate-pulse">↓ Falling</div>
          )}
          {priceDirection.no === 'up' && (
            <div className="text-xs text-red-400 mt-1 animate-pulse">↑ Rising</div>
          )}
          </div>
          </div>
          
          {/* Visual Progress Bar */}
          <div className="w-full bg-[#052393]/30 rounded-full h-3 mb-2 overflow-hidden">
          <div 
          className="h-3 rounded-full transition-all duration-500 ease-out"
          style={{
          background: `linear-gradient(to right, #009245 0%, #009245 ${yesPercent}%, #ef4444 ${yesPercent}%, #ef4444 100%)`
          }}
          ></div>
          </div>
          <div className="flex justify-between text-xs font-bold text-gray-300">
          <span className="text-cpx-green">YES {yesPercent}%</span>
          <span className="text-red-400">NO {noPercent}%</span>
          </div>
          </div>
          )}

          {/* SECTION 2: Odds Over Time Chart - Show this SECOND (NOT for AGI markets) */}
          {!(market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="card p-6 mb-6  border-[#052393]/40">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-black text-white">📈 Price Movement {priceHistory.length > 2 && <span className="text-xs ml-2 text-yellow-400 animate-pulse">LIVE</span>}</h3>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setTimeRange('24h')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  timeRange === '24h' 
                    ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393] text-white' 
                    : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
                }`}
              >
                24H
              </button>
              <button
                onClick={() => setTimeRange('7d')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  timeRange === '7d' 
                    ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393] text-white' 
                    : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
                }`}
              >
                7D
              </button>
              <button
                onClick={() => setTimeRange('30d')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  timeRange === '30d' 
                    ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393] text-white' 
                    : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
                }`}
              >
                30D
              </button>
              <button
                onClick={() => setTimeRange('all')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  timeRange === 'all' 
                    ? 'bg-gradient-to-r from-[#2AAAEA] to-[#052393] text-white' 
                    : 'bg-[#052393]/20 text-gray-400 hover:bg-[#052393]/40'
                }`}
              >
                ALL
              </button>
            </div>
          </div>
          
          {/* Line chart showing odds over time */}
          <div className="h-80 bg-[#001a3d] rounded-lg border border-[#052393]/40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={priceHistory.length > 0 ? priceHistory : [
                  { time: 'Start', yes: '50', no: '50' },
                  { time: 'Now', yes: yesPercent, no: noPercent },
                ]}
                margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
              >
                <XAxis 
                  dataKey="time" 
                  stroke="#6B7280"
                  style={{ fontSize: '12px', fontWeight: 'bold' }}
                />
                <YAxis 
                  stroke="#6B7280"
                  style={{ fontSize: '12px', fontWeight: 'bold' }}
                  domain={[0, 100]}
                  ticks={[0, 25, 50, 75, 100]}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#001233',
                    border: '1px solid #2AAAEA',
                    borderRadius: '8px',
                    color: '#fff'
                  }}
                  labelStyle={{ color: '#2AAAEA', fontWeight: 'bold' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  iconType="circle"
                />
                <Line 
                  type="monotone" 
                  dataKey="yes" 
                  stroke="#009245" 
                  strokeWidth={3}
                  name="Yes"
                  dot={{ fill: '#009245', r: 5 }}
                  activeDot={{ r: 7 }}
                />
                <Line 
                  type="monotone" 
                  dataKey="no" 
                  stroke="#ef4444" 
                  strokeWidth={3}
                  name="No"
                  dot={{ fill: '#ef4444', r: 5 }}
                  activeDot={{ r: 7 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          </div>
          )}
          
          </div>
          )}
                      
          {/* TAB: Chat */}
          {activeTab === 'chat' && (
          <div>
          <MarketChat marketId={id || ''} creatorAddress={market?.creator_address} />
          </div>
          )}
                      
          {/* TAB: Stats */}
          {activeTab === 'stats' && (
          <div className="card p-6">
          <h2 className="text-xl font-semibold text-white mb-6">📊 Market Statistics</h2>
                          
          <div className="space-y-4">
          <div className="bg-gray-900/50 p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">👤 Creator</h3>
          <div className="text-white font-mono text-sm">{market.creator_name || market.creator_email || (market.creator_address?.startsWith('agi_email_') ? 'AGI User' : market.creator_address) || 'Unknown'}</div>
          </div>
                            
          <div className="bg-[#009245]/5 border border-[#009245]/20 p-4 rounded-lg">
          <h3 className="text-sm font-bold text-white mb-3">✅ YES Bettors ({bettors.yes.length})</h3>
          {bettors.yes.length > 0 ? (
            <div className="space-y-2">
              {bettors.yes.map((bet: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm  p-2 rounded border border-[#052393]/30">
                  <span className="font-mono text-xs text-gray-400">
                    {/* Show email/name for AGI markets, wallet address for regular markets */}
                    {(market.is_manual === 1 || market.category === 'agi_manual')
                      ? (bet.bettor_name || bet.bettor_email || 'AGI User')
                      : `${bet.wallet_address?.substring(0, 4)}...${bet.wallet_address?.substring(bet.wallet_address?.length - 4)}`
                    }
                  </span>
                  <span className="text-cpx-green font-bold">
                    {bet.net_bet_amount?.toLocaleString() || bet.amount?.toLocaleString()} {(market.is_manual === 1 || market.category === 'agi_manual') ? 'credits' : 'tokens'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">No YES bets yet</div>
          )}
          </div>
                            
          <div className="bg-red-900/5 border border-red-500/20 p-4 rounded-lg">
          <h3 className="text-sm font-bold text-white mb-3">❌ NO Bettors ({bettors.no.length})</h3>
          {bettors.no.length > 0 ? (
            <div className="space-y-2">
              {bettors.no.map((bet: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm  p-2 rounded border border-[#052393]/30">
                  <span className="font-mono text-xs text-gray-400">
                    {/* Show email/name for AGI markets, wallet address for regular markets */}
                    {(market.is_manual === 1 || market.category === 'agi_manual')
                      ? (bet.bettor_name || bet.bettor_email || 'AGI User')
                      : `${bet.wallet_address?.substring(0, 4)}...${bet.wallet_address?.substring(bet.wallet_address?.length - 4)}`
                    }
                  </span>
                  <span className="text-red-400 font-bold">
                    {bet.net_bet_amount?.toLocaleString() || bet.amount?.toLocaleString()} {(market.is_manual === 1 || market.category === 'agi_manual') ? 'credits' : 'tokens'}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-400 text-sm">No NO bets yet</div>
          )}
          </div>

          {/* AI ODDS SECTION */}
          {market.ai_odds_yes && market.ai_odds_no && (
          <div className="bg-[#052393]/20 p-4 rounded-lg border border-[#2AAAEA]/30">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-white flex items-center">
                🎲 AI Market Odds
              </h3>
              {market.odds_reasoning_parsed && (
                <button
                  onClick={() => setShowOddsReasoningModal(true)}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="View AI reasoning"
                >
                  <Info className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-2">
              <div className="bg-green-500/10 p-3 rounded border border-green-500/30 text-center">
                <div className="text-xs text-gray-400 mb-1">YES</div>
                <div className="text-2xl font-black text-green-400">
                  {Math.round((market.ai_odds_yes || 0.5) * 100)}%
                </div>
              </div>
              <div className="bg-red-500/10 p-3 rounded border border-red-500/30 text-center">
                <div className="text-xs text-gray-400 mb-1">NO</div>
                <div className="text-2xl font-black text-red-400">
                  {Math.round((market.ai_odds_no || 0.5) * 100)}%
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-400 text-center">
              AI Confidence: {market.odds_confidence || 0}%
            </div>
          </div>
          )}
                            
          {/* Blockchain section - ONLY for non-AGI markets */}
          {!(market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="bg-gray-900/50 p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">⛓️ Blockchain</h3>
          <div className="space-y-2 text-sm">
          <div className="flex justify-between">
          <span className="text-gray-400">Market ID:</span>
          <span className="text-white font-mono">{market.id}</span>
          </div>
          <div className="flex justify-between">
          <span className="text-gray-400">Total Volume:</span>
          <span className="text-white">{totalPool?.toLocaleString() || 0} tokens</span>
          </div>
          <div className="flex justify-between">
          <span className="text-gray-400">YES Pool:</span>
          <span className="text-emerald-400">{yesAmount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
          <span className="text-gray-400">NO Pool:</span>
          <span className="text-rose-400">{noAmount.toLocaleString()}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-gray-700">
          <span className="text-gray-400">On-Chain Account:</span>
          {(() => {
            try {
              // Derive the actual market PDA from the market ID
              const PROGRAM_ID = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
              const marketIdBytes = new Uint8Array(8)
              new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(market.id), true)
              const [marketPDA] = PublicKey.findProgramAddressSync(
                [Buffer.from("market"), marketIdBytes.slice(0, 6)],
                PROGRAM_ID
              )
              return (
                <a
                  href={`https://explorer.solana.com/address/${marketPDA.toString()}?cluster=devnet`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:text-cyan-300 text-xs font-mono flex items-center space-x-1"
                >
                  <span>{marketPDA.toString().slice(0, 4)}...{marketPDA.toString().slice(-4)}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              )
            } catch (e) {
              return <span className="text-gray-500 text-xs">Unable to derive address</span>
            }
          })()}
          </div>
          </div>
          </div>
          )}
          
          {/* AGI Credits section - ONLY for AGI markets */}
          {(market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
          <h3 className="text-sm font-semibold text-yellow-400 mb-3">✨ AGI Credits</h3>
          <div className="space-y-2 text-sm">
          <div className="flex justify-between">
          <span className="text-gray-400">Market ID:</span>
          <span className="text-white font-mono">{market.id}</span>
          </div>
          <div className="flex justify-between">
          <span className="text-gray-400">Total Volume:</span>
          <span className="text-white">{(market.total_yes_bets || 0) + (market.total_no_bets || 0)} credits</span>
          </div>
          <div className="flex justify-between">
          <span className="text-gray-400">YES Pool:</span>
          <span className="text-emerald-400">{market.total_yes_bets || 0} credits</span>
          </div>
          <div className="flex justify-between">
          <span className="text-gray-400">NO Pool:</span>
          <span className="text-rose-400">{market.total_no_bets || 0} credits</span>
          </div>
          </div>
          </div>
          )}
          </div>
          </div>
          )}
                      
          {/* TAB: Resolution Rules - For AGI Markets */}
          {activeTab === 'plan' && (market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="space-y-6">
          
          {/* AGI Resolution Rules */}
          <div className="card p-6 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/40">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <FileText className="w-5 h-5 mr-2 text-yellow-400" />
          📋 Resolution Rules - How This Market Will Be Resolved
          </h3>
          
          <div className="bg-gray-900/50 p-6 rounded-lg border border-yellow-500/20">
            <p className="text-white whitespace-pre-wrap leading-relaxed">
              {market.resolution_rules || 'No resolution rules specified.'}
            </p>
          </div>
          
          <div className="mt-6 grid md:grid-cols-2 gap-4">
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-4 rounded-lg">
              <span className="font-bold text-yellow-400 uppercase tracking-wider text-xs">Resolution Method:</span>
              <p className="text-white mt-2 font-medium">Manual - Admin Decision</p>
              <p className="text-gray-400 text-sm mt-1">An AGI admin will manually resolve this market based on the rules above.</p>
            </div>
            
            <div className="bg-purple-500/10 border border-purple-500/30 p-4 rounded-lg">
              <span className="font-bold text-purple-400 uppercase tracking-wider text-xs">Betting Currency:</span>
              <p className="text-white mt-2 font-medium">AGI Credits</p>
              <p className="text-gray-400 text-sm mt-1">This is an internal AGI market using credits, not blockchain tokens.</p>
            </div>
          </div>
          
          <div className="mt-4 bg-blue-500/10 border border-blue-500/30 p-4 rounded-lg">
            <span className="font-bold text-blue-400 uppercase tracking-wider text-xs">⏰ Timeline:</span>
            <div className="mt-2 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-400">Betting Ends:</span>
                <span className="text-white font-medium">{new Date(market.betting_ends_at).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Resolution Time:</span>
                <span className="text-white font-medium">{new Date(market.resolution_time).toLocaleString()}</span>
              </div>
            </div>
          </div>
          </div>
          </div>
          )}
          
          {/* TAB: AI Insight - For AGI Markets */}
          {activeTab === 'ai_insight' && (market.is_manual === 1 || market.category === 'agi_manual') && market.agi_odds_reasoning && (
          <div className="space-y-6">
            <div className="card p-6 bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border-2 border-yellow-500/40">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Sparkles className="w-5 h-5 mr-2 text-yellow-400" />
                🎲 AI Odds Analysis
              </h3>
              
              {(() => {
                try {
                  const odds = JSON.parse(market.agi_odds_reasoning);
                  return (
                    <div className="space-y-6">
                      {/* Odds Display */}
                      <div className="flex items-center justify-center space-x-8 mb-6">
                        <div className="text-center">
                          <div className="text-4xl font-black text-emerald-400">{odds.odds?.yes || 50}%</div>
                          <div className="text-sm text-gray-400 uppercase tracking-wider mt-1">YES</div>
                        </div>
                        <div className="text-2xl text-gray-600">vs</div>
                        <div className="text-center">
                          <div className="text-4xl font-black text-rose-400">{odds.odds?.no || 50}%</div>
                          <div className="text-sm text-gray-400 uppercase tracking-wider mt-1">NO</div>
                        </div>
                      </div>
                      
                      {/* Confidence Badge */}
                      {odds.confidence && (
                        <div className="text-center mb-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${
                            odds.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                            odds.confidence === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {odds.confidence} confidence
                          </span>
                        </div>
                      )}
                      
                      {/* Reasoning */}
                      <div className="bg-gray-900/50 p-4 rounded-lg border border-yellow-500/20">
                        <h4 className="text-sm font-bold text-yellow-400 uppercase tracking-wider mb-2">Analysis</h4>
                        <p className="text-white leading-relaxed">{odds.reasoning || 'No reasoning provided.'}</p>
                      </div>
                      
                      {/* Fun Comment */}
                      {odds.funComment && (
                        <div className="bg-purple-500/10 p-4 rounded-lg border border-purple-500/20">
                          <p className="text-purple-300 italic text-lg">"{odds.funComment}"</p>
                        </div>
                      )}
                      
                      {/* Wildcard */}
                      {odds.wildcard && (
                        <div className="bg-rose-500/10 p-4 rounded-lg border border-rose-500/20">
                          <h4 className="text-sm font-bold text-rose-400 uppercase tracking-wider mb-2">⚠️ Wildcard Factor</h4>
                          <p className="text-rose-300">{odds.wildcard}</p>
                        </div>
                      )}
                      
                      {/* Team Members Mentioned */}
                      {odds.teamMentioned && odds.teamMentioned.length > 0 && (
                        <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/20">
                          <h4 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-2">👥 Team Members in Play</h4>
                          <div className="flex flex-wrap gap-2">
                            {odds.teamMentioned.map((name: string, i: number) => (
                              <span key={i} className="px-3 py-1 bg-blue-500/20 rounded-full text-blue-300 text-sm font-medium">
                                {name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Admin: Regenerate AI Odds */}
                      {agiUser?.isAdmin && (
                        <div className="mt-6 pt-6 border-t border-yellow-500/30">
                          <div className="flex items-center space-x-2 mb-4">
                            <Shield className="w-5 h-5 text-purple-400" />
                            <span className="font-bold text-purple-400">Admin: Regenerate Analysis</span>
                          </div>
                          <p className="text-gray-400 text-sm mb-3">
                            If the analysis above is too racy or inappropriate, enter instructions for the AI and regenerate.
                          </p>
                          <div className="space-y-3">
                            <textarea
                              value={regenerateInstructions}
                              onChange={(e) => setRegenerateInstructions(e.target.value)}
                              placeholder="e.g., Don't mention drinking, keep it professional, avoid jokes about..."
                              className="w-full bg-gray-900/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 resize-none"
                              rows={2}
                            />
                            <button
                              onClick={handleRegenerateOdds}
                              disabled={isRegeneratingOdds || !regenerateInstructions.trim()}
                              className="w-full py-3 rounded-xl bg-purple-500/20 border border-purple-500/50 text-purple-400 font-bold hover:bg-purple-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                            >
                              {isRegeneratingOdds ? (
                                <>
                                  <RefreshCw className="w-5 h-5 animate-spin" />
                                  <span>Regenerating...</span>
                                </>
                              ) : (
                                <>
                                  <RefreshCw className="w-5 h-5" />
                                  <span>Regenerate AI Odds</span>
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                } catch (e) {
                  return (
                    <div className="bg-gray-900/50 p-4 rounded-lg border border-gray-700">
                      <p className="text-gray-400">Unable to parse AI odds data.</p>
                    </div>
                  );
                }
              })()}
            </div>
          </div>
          )}
          
          {/* TAB: Validation Plan - For Regular Markets */}
          {activeTab === 'plan' && validationPlan && !(market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="space-y-6">
          
          {/* AI Validation Details */}
          <div className="card p-6 bg-gradient-to-r from-[#052393]/20 to-[#2AAAEA]/10 border-2 border-[#2AAAEA]/40">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <Brain className="w-5 h-5 mr-2 text-cpx-light-blue" />
          🎯 AI Validation Details - How This Market Will Resolve
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
          {/* Clean Question */}
          {(validationPlan?.validations?.[0]?.validation_plan?.clean_question || 
          validationPlan?.validations?.[0]?.validation_plan?.interpretation?.clean_question ||
          market?.question) && (
          <div className="bg-[#052393]/30 border border-[#2AAAEA]/30 p-3 rounded-lg">
          <span className="font-bold text-cpx-light-blue uppercase tracking-wider text-xs">Clean Question:</span>
          <p className="text-white mt-1 font-medium text-sm">
          {validationPlan?.validations?.[0]?.validation_plan?.clean_question || 
          validationPlan?.validations?.[0]?.validation_plan?.interpretation?.clean_question ||
          market?.question}
          </p>
          </div>
          )}
          
          {/* Current Value */}
          {validationPlan?.validations?.[0]?.validation_plan?.interpretation?.current_value && (
          <div className="bg-[#009245]/10 border border-[#009245]/30 p-3 rounded-lg">
          <span className="font-bold text-cpx-green uppercase tracking-wider text-xs">Current Value:</span>
          <span className="ml-2 text-white font-bold text-lg">{validationPlan.validations[0].validation_plan.interpretation.current_value}</span>
          </div>
          )}
          
          {/* Target */}
          {validationPlan?.validations?.[0]?.validation_plan?.interpretation?.target && (
          <div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 p-3 rounded-lg">
          <span className="font-bold text-cpx-yellow uppercase tracking-wider text-xs">Target:</span>
          <span className="ml-2 text-white font-bold text-lg">{validationPlan.validations[0].validation_plan.interpretation.target}</span>
          </div>
          )}
          
          {/* Deadline */}
          {validationPlan?.validations?.[0]?.validation_plan?.interpretation?.deadline && (
          <div className="bg-[#2AAAEA]/10 border border-[#2AAAEA]/30 p-3 rounded-lg">
          <span className="font-bold text-cpx-light-blue uppercase tracking-wider text-xs">Deadline:</span>
          <span className="ml-2 text-white">{validationPlan.validations[0].validation_plan.interpretation.deadline}</span>
          </div>
          )}
          </div>
          
          {/* Resolution Method */}
          {(validationPlan?.validations?.[0]?.validation_plan?.interpretation?.methodology ||
          validationPlan?.validations?.[0]?.verification_method) && (
          <div className="bg-[#052393]/30 border border-[#2AAAEA]/30 p-3 rounded-lg mt-4">
          <span className="font-bold text-cpx-light-blue uppercase tracking-wider text-xs">Resolution Method:</span>
          <p className="text-white mt-1 text-sm">
          {validationPlan?.validations?.[0]?.validation_plan?.interpretation?.methodology || 
          validationPlan?.validations?.[0]?.verification_method}
          </p>
          </div>
          )}
          
          {/* AI Will Check */}
          {validationPlan?.validations?.[0]?.validation_plan?.interpretation?.assumptions && 
          validationPlan.validations[0].validation_plan.interpretation.assumptions.length > 0 && (
          <div className="bg-[#009245]/10 border border-[#009245]/30 p-3 rounded-lg mt-4">
          <span className="font-bold text-cpx-green uppercase tracking-wider text-xs">🔍 AI Will Check:</span>
          <ul className="list-disc list-inside text-white mt-2 space-y-1">
          {validationPlan.validations[0].validation_plan.interpretation.assumptions.map((assumption: string, i: number) => (
          <li key={i} className="text-sm">{assumption}</li>
          ))}
          </ul>
          </div>
          )}
          </div>
          
          <button
          onClick={() => setShowValidationPlan(true)}
          className="w-full bg-gradient-to-r from-[#2AAAEA] to-[#052393] hover:shadow-lg hover:shadow-[#2AAAEA]/50 text-white py-3 rounded-lg font-semibold transition-all"
          >
          📋 View Complete Technical Validation Plan (Full Details)
          </button>
          </div>
          )}
                      
          {/* TAB: Source Article */}
          {activeTab === 'article' && (market.source_article_url || market.source_article_data) && (
          <div className="space-y-6">
          
          {/* Article Header */}
          <div className="card p-6 bg-gradient-to-r from-[#FBB03B]/20 to-[#FF8C00]/10 border-2 border-[#FBB03B]/40">
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
          📰 Source Article - Where This Market Came From
          </h3>
          
          {(() => {
            try {
              const articleData = market.source_article_data ? JSON.parse(market.source_article_data) : null;
              return (
                <div className="space-y-4">
                  {/* Article Title */}
                  <div className="bg-[#052393]/30 border border-[#FBB03B]/30 p-4 rounded-lg">
                    <span className="font-bold text-cpx-yellow uppercase tracking-wider text-xs block mb-2">Article Title:</span>
                    <h4 className="text-white text-xl font-bold">{articleData?.title || 'Sports Article'}</h4>
                  </div>
                  
                  {/* Article Details */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {articleData?.source && (
                      <div className="bg-[#2AAAEA]/10 border border-[#2AAAEA]/30 p-3 rounded-lg">
                        <span className="font-bold text-cpx-light-blue uppercase tracking-wider text-xs">Source:</span>
                        <p className="text-white mt-1 font-medium">{articleData.source}</p>
                      </div>
                    )}
                    
                    {articleData?.publishedAt && (
                      <div className="bg-[#009245]/10 border border-[#009245]/30 p-3 rounded-lg">
                        <span className="font-bold text-cpx-green uppercase tracking-wider text-xs">Published:</span>
                        <p className="text-white mt-1">{new Date(articleData.publishedAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Article Summary */}
                  {articleData?.summary && (
                    <div className="bg-[#052393]/30 border border-[#FBB03B]/30 p-4 rounded-lg">
                      <span className="font-bold text-cpx-yellow uppercase tracking-wider text-xs block mb-2">Summary:</span>
                      <p className="text-gray-300 text-sm leading-relaxed">{articleData.summary}</p>
                    </div>
                  )}
                  
                  {/* Read Full Article Button */}
                  <div className="pt-4">
                    <a
                      href={market.source_article_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FBB03B] to-[#FF8C00] 
                               text-white font-bold rounded-lg hover:shadow-lg hover:shadow-[#FBB03B]/50 transition-all"
                    >
                      📄 Read Full Article
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>
                  
                  {/* AI Notice */}
                  <div className="bg-blue-500/10 border border-blue-400/30 p-4 rounded-lg">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl">🤖</span>
                      <div>
                        <p className="text-blue-300 font-semibold mb-1">Auto-Generated Market</p>
                        <p className="text-gray-400 text-sm">
                          This prediction market was automatically created by AI after analyzing this sports article. 
                          The AI identified a verifiable prediction opportunity and set up the validation rules.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            } catch (error) {
              console.error('Failed to parse article data:', error);
              return (
                <div className="text-gray-400">
                  <p>Article data unavailable</p>
                  <a
                    href={market.source_article_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-cpx-light-blue hover:underline"
                  >
                    View original article →
                  </a>
                </div>
              );
            }
          })()}
          </div>
          
          </div>
          )}
                      
          {/* TAB: Resolution (when resolved OR disputed) */}
          {activeTab === 'resolution' && (market.status === 'resolved' || market.status === 'disputed') && (
          <div className="space-y-6">
          {/* Resolution Summary Card - ONLY when resolved */}
          {market.status === 'resolved' && (
          <div className={`card p-6 border-2 ${(market.outcome || market.winningOutcome) === 1 ? 'bg-[#009245]/10 border-[#009245]' : 'bg-red-900/10 border-red-500'}`}>
          <div className="flex items-center space-x-3 mb-6">
          <CheckCircle className={`w-8 h-8 ${(market.outcome || market.winningOutcome) === 1 ? 'text-cpx-green' : 'text-red-400'}`} />
          <div className="flex-1">
          <h3 className="text-2xl font-black text-white mb-2">🎉 Market Resolved!</h3>
          <div className="flex items-center space-x-2">
          <span className="text-white font-bold">Winning Outcome:</span>
          <span className={`text-2xl font-black ${(market.outcome || market.winningOutcome) === 1 ? 'text-cpx-green' : 'text-red-400'}`}>
          {(market.outcome || market.winningOutcome) === 1 ? 'YES ✅' : 'NO ❌'}
          </span>
          </div>
          </div>
          </div>
          
          {market.adjudicator_reasoning && (
          <div className="bg-[#052393]/20 border border-[#2AAAEA]/30 p-4 rounded-lg">
          <h4 className="font-bold text-cpx-light-blue mb-2 uppercase tracking-wider text-sm">📊 Resolution Summary</h4>
          <p className="text-white text-sm leading-relaxed">
          {market.adjudicator_reasoning
          .split('. Source:')[0]
          .replace(/AI Oracle:/gi, '')
          .replace(/Claude Opus/gi, 'ARX Agent 1')
          .replace(/Claude Sonnet/gi, 'ARX Agent 2')
          .replace(/Claude/gi, 'ARX Agent')
          .replace(/Perplexity/gi, 'ARX Agent 6')
          .replace(/GPT-4/gi, 'ARX Agent')
          .replace(/GPT/gi, 'ARX Agent')
          .replace(/OpenAI/gi, 'ARX')
          .replace(/Anthropic/gi, 'ARX')
          .replace(/perplexity_search/gi, 'web search')
          .replace(/perplexity/gi, 'web search')
          .replace(/_search/gi, '')
          .trim()}
          </p>
          </div>
          )}
          <button
          onClick={async () => {
          try {
          const response = await axios.get(`/api/oracle/markets/${id}/evidence`)
          setResolutionEvidence(response.data.resolution?.evidence || [])
          setShowResolutionModal(true)
          } catch (error) {
          console.error('Failed to load resolution evidence:', error)
          }
          }}
          className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all mt-4"
          >
          📊 View Full Resolution Details & Evidence
          </button>
          </div>
          )}
          
          {/* DISPUTE content - inside Resolution tab */}
          {market.status === 'disputed' && (
          <div className="card p-6 mb-6 bg-[#FBB03B]/10 border-2 border-[#FBB03B]/40">
          <div className="flex items-center space-x-3 mb-4">
          <AlertTriangle className="w-6 h-6 text-cpx-yellow" />
          <div>
          <h3 className="font-semibold text-cpx-yellow">⚠️ Awaiting Human Review</h3>
          <p className="text-white text-sm">
          AI validators could not reach consensus. Awaiting administrator review.
          </p>
          {market.adjudicator_reasoning && (
          <p className="text-gray-200 text-xs mt-1">
          {market.adjudicator_reasoning
          .split('. Source:')[0]
          .replace(/AI Oracle:/gi, '')
          .replace(/Claude Opus/gi, 'ARX Agent 1')
          .replace(/Claude Sonnet/gi, 'ARX Agent 2')
          .replace(/Claude/gi, 'ARX Agent')
          .replace(/Perplexity/gi, 'ARX Agent 6')
          .replace(/GPT-4/gi, 'ARX Agent')
          .replace(/GPT/gi, 'ARX Agent')
          .replace(/OpenAI/gi, 'ARX')
          .replace(/Anthropic/gi, 'ARX')
          .replace(/perplexity_search/gi, 'web search')
          .replace(/perplexity/gi, 'web search')
          .replace(/_search/gi, '')}
          </p>
          )}
          </div>
          </div>
                          
          {/* View Evidence Button */}
          <button
          onClick={async () => {
          try {
          const evidenceRes = await axios.get(`/api/oracle/markets/${id}/evidence`)
          setResolutionEvidence(evidenceRes.data.resolution?.evidence || evidenceRes.data.evidence || [])
          setShowResolutionModal(true)
          } catch (err) {
          toast.error('Failed to load evidence')
          }
          }}
          className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center space-x-2 mb-4"
          >
          <Award className="w-5 h-5" />
          <span>📊 View Evidence & Validator Reports</span>
          </button>
          {/* Admin note */}
          <div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 rounded-lg p-4 text-sm text-gray-300">
          💡 <strong>Note:</strong> Once an administrator resolves this market, winners will be able to claim their rewards.
          </div>
          </div>
          )}
          </div>
          )}
          
          {/* TAB: My Winnings - Smart tab only for winners! */}
          {activeTab === 'winnings' && connected && userIsWinner && market.status === 'resolved' && (
          <div className="space-y-6">
          
          {/* Winner Celebration Card */}
          <div className="card p-8 bg-gradient-to-br from-[#009245]/20 to-emerald-900/20 border-2 border-[#009245]">
          <div className="text-center mb-6">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-black text-white mb-2">Congratulations, You Won!</h2>
          <p className="text-cpx-green text-lg font-bold">
          You correctly predicted: <span className={`text-2xl ${(market.outcome || market.winningOutcome) === 1 ? 'text-cpx-green' : 'text-red-400'}`}>
          {(market.outcome || market.winningOutcome) === 1 ? 'YES ✅' : 'NO ❌'}
          </span>
          </p>
          </div>
          
          {/* Payout Information */}
          <div className="bg-[#052393]/30 border border-[#2AAAEA]/40 rounded-xl p-6 mb-6">
          <h3 className="text-xl font-black text-cpx-light-blue mb-4 flex items-center">
          <Users className="w-6 h-6 mr-2" />
          💰 Payout Information
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
          <div className={`p-6 rounded-lg border-2 ${(market.outcome || market.winningOutcome) === 1 ? 'bg-[#009245]/10 border-[#009245]' : 'bg-[#001a3d] border-[#052393]/40'}`}>
          <div className="text-sm text-gray-300 font-medium uppercase tracking-wider">YES Pool</div>
          <div className="text-3xl font-black text-white my-2">
          {(market.is_manual === 1 || market.category === 'agi_manual') 
            ? (market.total_yes_bets || 0).toLocaleString()
            : (market.total_yes || market.totalYes || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400">{(market.is_manual === 1 || market.category === 'agi_manual') ? 'credits' : 'tokens'}</div>
          <div className={`text-sm font-semibold mt-3 ${(market.outcome || market.winningOutcome) === 1 ? 'text-cpx-green' : 'text-red-400'}`}>
          {(market.outcome || market.winningOutcome) === 1 ? '✅ Winners' : '❌ Losers'}
          </div>
          </div>
          
          <div className={`p-6 rounded-lg border-2 ${(market.outcome || market.winningOutcome) === 0 ? 'bg-red-900/10 border-red-500' : 'bg-[#001a3d] border-[#052393]/40'}`}>
          <div className="text-sm text-gray-300 font-medium uppercase tracking-wider">NO Pool</div>
          <div className="text-3xl font-black text-white my-2">
          {(market.is_manual === 1 || market.category === 'agi_manual')
            ? (market.total_no_bets || 0).toLocaleString()
            : (market.total_no || market.totalNo || 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-400">{(market.is_manual === 1 || market.category === 'agi_manual') ? 'credits' : 'tokens'}</div>
          <div className={`text-sm font-semibold mt-3 ${(market.outcome || market.winningOutcome) === 0 ? 'text-cpx-green' : 'text-red-400'}`}>
          {(market.outcome || market.winningOutcome) === 0 ? '✅ Winners' : '❌ Losers'}
          </div>
          </div>
          </div>
          
          {/* Blockchain Proof - ONLY for non-AGI markets */}
          {!(market.is_manual === 1 || market.category === 'agi_manual') && (
          <div className="mt-6 bg-[#001a3d] border border-[#2AAAEA]/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
          <h4 className="font-bold text-cpx-light-blue uppercase tracking-wider text-xs">🔗 Blockchain Proof</h4>
          <button
          onClick={async () => {
          setCheckingClaimed(true)
          try {
          // Using connection from useConnection() hook
          const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
          
          const marketIdNum = parseInt(id || '0')
          const marketIdBytes = new Uint8Array(8)
          new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
          
          const [marketPDA] = PublicKey.findProgramAddressSync(
          [Buffer.from("market"), marketIdBytes.slice(0, 6)],
          programId
          )
          
          const [userPositionPDA] = PublicKey.findProgramAddressSync(
          [Buffer.from("position"), marketPDA.toBuffer(), publicKey!.toBuffer()],
          programId
          )
          
          const positionAccount = await connection.getAccountInfo(userPositionPDA)
          if (!positionAccount) {
          console.log('⚠️ No position account')
          setWinningsClaimed(false)
          toast.error('No position account found')
          } else if (positionAccount.data.length > 0) {
          const dataView = new DataView(positionAccount.data.buffer, positionAccount.data.byteOffset)
          
          // CORRECT: claimed flag at byte 89!
          const claimedFlag = positionAccount.data.length > 89 ? positionAccount.data[89] : 0
          const outcome = positionAccount.data[72]
          const amount = Number(dataView.getBigUint64(73, true))
          
          console.log('🔍 REFRESH CHECK:', { 
            claimedFlag, 
            claimedFlagByte89: positionAccount.data[89],
            outcome,
            amount
          })
          
          if (claimedFlag === 1) {
          setWinningsClaimed(true)
          toast.success('✅ Confirmed: Already claimed!')
          } else {
          setWinningsClaimed(false)
          toast.success('💰 Ready to claim!')
          }
          } else {
          setWinningsClaimed(false)
          toast.error('Position account has no data')
          }
          } catch (error) {
          console.error('Failed to check status:', error)
          toast.error('Failed to check claim status')
          } finally {
          setCheckingClaimed(false)
          }
          }}
          disabled={checkingClaimed}
          className="text-xs bg-[#2AAAEA]/20 hover:bg-[#2AAAEA]/30 text-cpx-light-blue px-3 py-1 rounded transition-colors disabled:opacity-50"
          >
          {checkingClaimed ? 'Checking...' : '🔄 Refresh Status'}
          </button>
          </div>
          <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between">
          <span className="text-gray-300">Market ID:</span>
          <span className="font-mono text-white">{market.id}</span>
          </div>
          <div className="flex items-center justify-between">
          <span className="text-gray-300">Your Wallet:</span>
          <span className="font-mono text-xs text-cpx-light-blue">
          {publicKey?.toString().slice(0, 4)}...{publicKey?.toString().slice(-4)}
          </span>
          </div>
          <div className="flex items-center justify-between">
          <span className="text-gray-300">Claim Status:</span>
          <span className={`font-bold text-xs ${winningsClaimed ? 'text-cpx-green' : 'text-cpx-yellow'}`}>
          {checkingClaimed ? 'Checking...' : winningsClaimed ? '✅ Claimed' : '💰 Ready'}
          </span>
          </div>
          </div>
          
          {/* Debug: Show position account data */}
          <details className="mt-3">
          <summary className="text-xs text-gray-400 cursor-pointer hover:text-gray-300">
          🔧 Debug: View Position Account Data
          </summary>
          <div className="mt-2 bg-[#001233] p-3 rounded text-xs">
          <button
          onClick={async () => {
          try {
          // Using connection from useConnection() hook
          const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
          const marketIdNum = parseInt(id || '0')
          const marketIdBytes = new Uint8Array(8)
          new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
          const [marketPDA] = PublicKey.findProgramAddressSync([Buffer.from("market"), marketIdBytes.slice(0, 6)], programId)
          const [userPositionPDA] = PublicKey.findProgramAddressSync([Buffer.from("position"), marketPDA.toBuffer(), publicKey!.toBuffer()], programId)
          
          const positionAccount = await connection.getAccountInfo(userPositionPDA)
          console.log('🔍 POSITION ACCOUNT FULL DATA:', positionAccount)
          if (positionAccount) {
          console.log('Data length:', positionAccount.data.length)
          console.log('Data bytes (all 90):', Array.from(positionAccount.data))
          if (positionAccount.data.length >= 90) {
          const dataView = new DataView(positionAccount.data.buffer, positionAccount.data.byteOffset)
          console.log('=== POSITION ACCOUNT STRUCTURE (from lib.rs) ===')
          console.log('Discriminator (bytes 0-7):', Array.from(positionAccount.data.slice(0, 8)))
          console.log('User pubkey (bytes 8-39):', Array.from(positionAccount.data.slice(8, 40)))
          console.log('Market pubkey (bytes 40-71):', Array.from(positionAccount.data.slice(40, 72)))
          console.log('Outcome (byte 72):', positionAccount.data[72], '(1=YES, 0=NO)')
          console.log('Amount (bytes 73-80):', Number(dataView.getBigUint64(73, true)), 'tokens')
          console.log('Timestamp (bytes 81-88):', Number(dataView.getBigInt64(81, true)))
          console.log('⭐⭐⭐ CLAIMED FLAG (byte 89):', positionAccount.data[89], positionAccount.data[89] === 1 ? '← ALREADY CLAIMED! ✅' : '← NOT CLAIMED YET 💰')
          console.log('===================================')
          
          // Auto-update UI based on what we found
          if (positionAccount.data[89] === 1) {
          setWinningsClaimed(true)
          toast.success('✅ Blockchain confirms: Already claimed!')
          } else {
          setWinningsClaimed(false)
          toast.success('💰 Blockchain confirms: Ready to claim!')
          }
          }
          }
          toast.success('Check browser console for details!')
          } catch (error) {
          console.error('Debug error:', error)
          toast.error('Check console for error')
          }
          }}
          className="text-cpx-light-blue hover:text-white text-xs underline"
          >
          Click to log position account to console
          </button>
          </div>
          </details>
          </div>
          )}
          </div>
          
          {/* Claim Button or Already Claimed Message - ONLY for non-AGI markets */}
          {!(market.is_manual === 1 || market.category === 'agi_manual') && (
          winningsClaimed ? (
          <div className="bg-[#009245]/20 border-2 border-[#009245] rounded-xl p-8 text-center">
          <div className="text-5xl mb-4">✅</div>
          <h3 className="text-2xl font-black text-cpx-green mb-2">Winnings Already Claimed!</h3>
          <p className="text-white text-sm">
          You've successfully claimed your winnings from this market.
          </p>
          <div className="mt-4 text-xs text-gray-300">
          Check your wallet balance to see your tokens.
          </div>
          </div>
          ) : (
          <button
          onClick={async () => {
          try {
          toast.loading('Claiming winnings...', { id: 'claim' })
                    
          // Using connection from useConnection() hook
          const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
          const tokenMint = new PublicKey('5mbkMp8x4aZ9ntsVPPnVUFekiE1joGU4gSANfsTtiiaT')
                    
          const marketIdNum = parseInt(id || '0')
          const marketIdBytes = new Uint8Array(8)
          new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
                    
          const [configPDA] = PublicKey.findProgramAddressSync([Buffer.from("config")], programId)
          const [marketPDA] = PublicKey.findProgramAddressSync([Buffer.from("market"), marketIdBytes.slice(0, 6)], programId)
          const [userPositionPDA] = PublicKey.findProgramAddressSync([Buffer.from("position"), marketPDA.toBuffer(), publicKey!.toBuffer()], programId)
          const [yesVaultAuth] = PublicKey.findProgramAddressSync([Buffer.from("yes_vault_auth"), marketPDA.toBuffer()], programId)
          const [noVaultAuth] = PublicKey.findProgramAddressSync([Buffer.from("no_vault_auth"), marketPDA.toBuffer()], programId)
                    
          const yesVault = await getAssociatedTokenAddress(tokenMint, yesVaultAuth, true)
          const noVault = await getAssociatedTokenAddress(tokenMint, noVaultAuth, true)
          const userTokenAccount = await getAssociatedTokenAddress(tokenMint, publicKey!)
                    
          const discriminator = Buffer.from([161, 215, 24, 59, 14, 236, 242, 221])
                    
          const accounts = [
          { pubkey: configPDA, isSigner: false, isWritable: false },
          { pubkey: marketPDA, isSigner: false, isWritable: true },
          { pubkey: yesVaultAuth, isSigner: false, isWritable: false },
          { pubkey: noVaultAuth, isSigner: false, isWritable: false },
          { pubkey: yesVault, isSigner: false, isWritable: true },
          { pubkey: noVault, isSigner: false, isWritable: true },
          { pubkey: tokenMint, isSigner: false, isWritable: false },
          { pubkey: publicKey!, isSigner: true, isWritable: true },
          { pubkey: userPositionPDA, isSigner: false, isWritable: true },
          { pubkey: userTokenAccount, isSigner: false, isWritable: true },
          { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }
          ]
                    
          const claimInstruction = new TransactionInstruction({
          keys: accounts,
          programId,
          data: discriminator
          })
                    
          const transaction = new Transaction().add(claimInstruction)
          transaction.feePayer = publicKey!
          const { blockhash } = await connection.getLatestBlockhash()
          transaction.recentBlockhash = blockhash
                    
          const signedTx = await (window as any).solana.signTransaction(transaction)
          const signature = await connection.sendRawTransaction(signedTx.serialize())
          const confirmation = await connection.confirmTransaction(signature, 'confirmed')
                    
          if (confirmation.value.err) {
          throw new Error('Claim failed')
          }
                    
          console.log('✅ Winnings claimed from blockchain!')
                    
          // Update database to record the claim
          try {
          const dbResponse = await axios.post(`/api/markets/${id}/claim`, {
          walletAddress: publicKey!.toString(),
          signature,
          claimed: true,
          claim_timestamp: new Date().toISOString()
          })
          console.log('✅ Claim recorded in database:', dbResponse.data)
          } catch (dbError) {
          console.error('⚠️ Failed to record claim in database:', dbError)
          // Continue anyway - blockchain claim succeeded
          }
                    
          console.log('🎉🎉🎉 CLAIM SUCCESSFUL - UPDATING UI TO SHOW CLAIMED!')
          toast.success('🎉 Winnings claimed successfully!', { id: 'claim' })
          
          // Mark as claimed in UI
          setWinningsClaimed(true)
                    
          } catch (error: any) {
          console.error('❌ CLAIM ERROR:', error)
          console.error('Error details:', JSON.stringify(error, null, 2))
          
          // Check if error is because already claimed
          const errorMsg = (error.message?.toLowerCase() || error.toString().toLowerCase() || '')
          const errorCode = error.code || error.error?.code || ''
          
          console.log('🔍 Checking if already claimed error...')
          console.log('Error message:', errorMsg)
          console.log('Error code:', errorCode)
          
          // The Solana contract throws: ErrorCode::AlreadyClaimed
          // This appears as "Already claimed" or error code 0x1775 (6005 in decimal)
          const alreadyClaimedIndicators = [
            'already claimed',  // Exact contract error message
            'alreadyclaimed',   // Without space
            '0x1775',           // Error code hex
            '6005',             // Error code decimal
            'custom program error: 0x1775',
            'account not found',
            'account does not exist',
          ]
          
          const isAlreadyClaimed = alreadyClaimedIndicators.some(indicator => 
            errorMsg.includes(indicator) || errorCode.toString().includes(indicator)
          )
          
          if (isAlreadyClaimed) {
          console.log('✅✅✅ BLOCKCHAIN REJECTED: ALREADY CLAIMED - UPDATING UI NOW!')
          setWinningsClaimed(true)
          toast.success('✅ Already claimed! UI updated.', { id: 'claim', duration: 5000 })
          } else {
          toast.error(error.message || 'Failed to claim', { id: 'claim' })
          }
          }
          }}
          disabled={checkingClaimed}
          className="w-full bg-gradient-to-r from-[#009245] to-emerald-600 hover:shadow-2xl hover:shadow-[#009245]/50 text-white text-xl py-6 rounded-xl font-black transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
          >
          {checkingClaimed ? 'Checking claim status...' : '💰 Claim Your Winnings from Blockchain'}
          </button>
          )
          )}
          
          {/* Help Text - Only show if not yet claimed and non-AGI market */}
          {!(market.is_manual === 1 || market.category === 'agi_manual') && !winningsClaimed && (
          <div className="bg-[#2AAAEA]/10 border border-[#2AAAEA]/30 rounded-lg p-4 text-sm">
          <p className="text-gray-200">
          <strong className="text-cpx-light-blue">ℹ️ How it works:</strong> Your winnings are securely stored on the Solana blockchain. 
          Click the button above to transfer them to your connected wallet. This is a one-time claim - make sure you're ready!
          </p>
          </div>
          )}
          
          </div>
          </div>
          )}
        </div>
      </div>

      {/* VALIDATION PLAN MODAL */}
      {showValidationPlan && validationPlan && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
<div className="bg-[#001233] border border-[#2AAAEA]/40 rounded-xl shadow-xl shadow-[#2AAAEA]/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
<div className="flex items-center justify-between p-6 border-b border-[#052393]/40 bg-gradient-to-r from-[#052393]/20 to-[#2AAAEA]/10">
<div className="flex items-center">
<img src="/cpx-logo.jpg" alt="CPX" className="w-8 h-8 rounded-lg mr-3" />
<h2 className="text-2xl font-bold text-white">AI Validation Plan</h2>
</div>
<button
onClick={() => setShowValidationPlan(false)}
className="p-2 hover:bg-gray-800 rounded-lg"
>
✖️
</button>
</div>
            
<div className="p-6">
<div className="space-y-6">

{/* BEAUTIFUL AI VALIDATION DETAILS - Enhanced version from CreateMarketPage */}
<div className="card p-6 border-2 border-[#FBB03B]">
<h3 className="text-2xl font-black text-white mb-4 flex items-center">
<img src="/cpx-logo.jpg" alt="CPX" className="w-8 h-8 rounded-lg mr-2" />
🎯 AI Validation Details - How This Pool Will Resolve
</h3>
                  
{/* Enhanced grid layout with more comprehensive data */}
<div className="grid md:grid-cols-2 gap-4">
{/* Clean Question - try multiple data sources */}
{(validationPlan?.validations?.[0]?.validation_plan?.clean_question || 
validationPlan?.validations?.[0]?.validation_plan?.interpretation?.clean_question ||
market?.question) && (
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B]">
<span className="font-bold text-cpx-yellow uppercase tracking-wider text-sm">CLEAN QUESTION:</span>
<p className="text-white mt-2 font-medium text-lg">
{validationPlan?.validations?.[0]?.validation_plan?.clean_question || 
validationPlan?.validations?.[0]?.validation_plan?.interpretation?.clean_question ||
market?.question}
</p>
</div>
)}
                    
{/* Current Value */}
{validationPlan?.validations?.[0]?.validation_plan?.interpretation?.current_value && (
<div className="bg-[#009245]/10 p-4 rounded-lg border border-[#009245]/30">
<span className="font-bold text-cpx-green uppercase tracking-wider text-xs">Current Value:</span>
<span className="ml-2 text-white font-black text-2xl">{validationPlan.validations[0].validation_plan.interpretation.current_value}</span>
</div>
)}
                    
{/* Target */}
{validationPlan?.validations?.[0]?.validation_plan?.interpretation?.target && (
<div className="bg-[#FBB03B]/10 p-4 rounded-lg border border-[#FBB03B]/30">
<span className="font-bold text-cpx-yellow uppercase tracking-wider text-xs">Target:</span>
<span className="ml-2 text-white font-black text-2xl">{validationPlan.validations[0].validation_plan.interpretation.target}</span>
</div>
)}
                    
{/* Deadline */}
{validationPlan?.validations?.[0]?.validation_plan?.interpretation?.deadline && (
<div className="bg-[#2AAAEA]/10 p-4 rounded-lg border border-[#2AAAEA]/30">
<span className="font-bold text-cpx-light-blue uppercase tracking-wider text-xs">Deadline:</span>
<span className="ml-2 text-white font-bold">{validationPlan.validations[0].validation_plan.interpretation.deadline}</span>
</div>
)}
</div>
                  
{/* Resolution Method */}
{(validationPlan?.validations?.[0]?.validation_plan?.interpretation?.methodology ||
validationPlan?.validations?.[0]?.verification_method) && (
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B] mt-4">
<span className="font-bold text-cpx-yellow uppercase tracking-wider text-sm">RESOLUTION METHOD:</span>
<p className="text-white mt-1">
{validationPlan?.validations?.[0]?.validation_plan?.interpretation?.methodology || 
validationPlan?.validations?.[0]?.verification_method}
</p>
</div>
)}
                  
{/* AI ASSUMPTIONS - DISPLAY ALL 8! */}
{validationPlan?.validations?.[0]?.validation_plan?.interpretation?.assumptions && (
<div className="bg-[#009245]/10 border border-[#009245]/30 p-3 rounded-lg shadow-sm mt-4">
<span className="font-bold text-cpx-green uppercase tracking-wider text-xs">🔍 AI Will Check - Assumptions ({Array.isArray(validationPlan.validations[0].validation_plan.interpretation.assumptions) ? validationPlan.validations[0].validation_plan.interpretation.assumptions.length : Object.keys(validationPlan.validations[0].validation_plan.interpretation.assumptions).length}):</span>
<ul className="list-disc list-inside text-white mt-2 space-y-2">
{(Array.isArray(validationPlan.validations[0].validation_plan.interpretation.assumptions) ? 
validationPlan.validations[0].validation_plan.interpretation.assumptions : 
Object.values(validationPlan.validations[0].validation_plan.interpretation.assumptions)).map((assumption: any, i: number) => (
<li key={i} className="text-sm leading-relaxed">{String(assumption)}</li>
))}
</ul>
</div>
)}
                  
{/* Data Sources */}
{validationPlan?.validations?.[0]?.validation_plan?.dataSources && 
validationPlan.validations[0].validation_plan.dataSources.length > 0 && (
<div className="bg-[#2AAAEA]/10 border border-[#2AAAEA]/30 p-3 rounded-lg shadow-sm mt-4">
<span className="font-bold text-cpx-light-blue uppercase tracking-wider text-xs">Data Sources:</span>
<div className="flex flex-wrap gap-2 mt-2">
{validationPlan.validations[0].validation_plan.dataSources.map((source: string, i: number) => (
<span key={i} className="px-2 py-1 bg-[#2AAAEA]/20 text-cpx-light-blue text-xs rounded font-bold border border-[#2AAAEA]/30">{source}</span>
))}
</div>
</div>
)}
                  
{/* Resolution Confidence - Add to top section */}
{validationPlan?.validations?.[0]?.confidence && (
<div className="bg-[#009245]/10 border border-[#009245]/30 p-3 rounded-lg shadow-sm mt-4">
<span className="font-bold text-cpx-green uppercase tracking-wider text-xs">Resolution Confidence:</span>
<span className="ml-2 text-cpx-green font-bold text-lg">{validationPlan.validations[0].confidence}%</span>
</div>
)}
                  
</div>
                
<div>
<h3 className="font-semibold text-white mb-2">🔍 Validation Evidence</h3>
<div className="space-y-4">
{/* CRITICAL TIMING INFO */}
<div className="bg-red-900/10 border-2 border-red-500/30 p-4 rounded-lg">
<h4 className="font-black text-red-400 text-lg mb-2 uppercase tracking-wider">⏰ RESOLUTION SCHEDULE</h4>
<div className="space-y-2 text-sm">
<div className="flex justify-between">
<span className="text-gray-400 font-semibold">Betting Ends:</span>
<span className="font-bold text-white">{new Date(market.betting_ends_at).toLocaleString()}</span>
</div>
<div className="flex justify-between">
<span className="text-gray-400 font-semibold">Resolution Time:</span>
<span className="font-bold text-white">{new Date(market.resolution_time).toLocaleString()}</span>
</div>
<div className="flex justify-between">
<span className="text-gray-400 font-semibold">Time Until Resolution:</span>
<span className="font-bold text-cpx-yellow">{
(() => {
const diff = new Date(market.resolution_time).getTime() - Date.now();
if (diff < 0) return "PAST DUE - SHOULD BE RESOLVING NOW!";
const hours = Math.floor(diff / (1000 * 60 * 60));
const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
const seconds = Math.floor((diff % (1000 * 60)) / 1000);
if (hours > 0) return `${hours}h ${minutes}m`;
if (minutes > 0) return `${minutes}m ${seconds}s`;
return `${seconds} seconds`;
})()
}</span>
</div>
</div>
</div>


{/* DETAILED TECHNICAL EXECUTION PLAN - ALWAYS SHOW THIS */}
<div className="space-y-3 text-sm">
{validationPlan?.validations?.[0] ? (
<>
{/* DETAILED EXECUTION PLAN - SHOW VALIDATION PLAN DETAILS */}
{validationPlan.validations[0].validation_plan && (
<div className="bg-[#052393]/10 p-4 rounded-lg border border-[#052393]/40">
<h4 className="font-bold text-white mb-3 uppercase tracking-wider text-sm">📋 Detailed Execution Plan:</h4>
<div className="space-y-2 text-sm text-gray-300">
                                
{/* Primary Method */}
<div>
<span className="font-medium">🎯 Primary Check:</span>
<div className="ml-4 mt-1">
<div>• <strong>Method:</strong> {validationPlan.validations[0].validation_plan.primaryMethod?.description}</div>
<div>• <strong>API:</strong> {validationPlan.validations[0].validation_plan.primaryMethod?.api?.replace(/perplexity_search/gi, 'Web Search').replace(/perplexity/gi, 'Web Search')}</div>
<div>• <strong>Query:</strong> {validationPlan.validations[0].validation_plan.primaryMethod?.searchQuery}</div>
</div>
</div>
                                
{/* Backup Methods */}
{validationPlan.validations[0].validation_plan.backupMethods && (
<div>
<span className="font-medium">🔄 Backup Methods:</span>
<div className="ml-4 mt-1">
{validationPlan.validations[0].validation_plan.backupMethods.map((method, i) => (
<div key={i}>• {method.description}</div>
))}
</div>
</div>
)}
                                
{/* Resolution Criteria */}
<div>
<span className="font-medium">✅ Resolution Logic:</span>
<div className="ml-4 mt-1">
<div>• <strong>YES if:</strong> {validationPlan.validations[0].validation_plan.resolutionCriteria?.positiveMatch}</div>
<div>• <strong>NO if:</strong> {validationPlan.validations[0].validation_plan.resolutionCriteria?.negativeMatch}</div>
</div>
</div>
                                
{/* Contingencies */}
{validationPlan.validations[0].validation_plan.contingencies && (
<div>
<span className="font-medium">⚠️ Edge Cases:</span>
<div className="ml-4 mt-1">
{Object.entries(validationPlan.validations[0].validation_plan.contingencies).map(([key, value]) => (
<div key={key}>• <strong>{key.replace(/([A-Z])/g, ' $1').toLowerCase()}:</strong> {String(value)}</div>
))}
</div>
</div>
)}
                                
{/* Tools Required */}
<div>
<span className="font-medium">🛠️ Tools Used:</span>
<div className="ml-4 mt-1">
{validationPlan.validations[0].validation_plan.toolsRequired?.map((tool, i) => (
<span key={i}>• {tool}{i < validationPlan.validations[0].validation_plan.toolsRequired.length - 1 ? ', ' : ''}</span>
))}
</div>
</div>
</div>
</div>
)}
                          
{/* RESOLUTION PLAN - THE STEP-BY-STEP PROCESS */}
{validationPlan?.validations?.[0]?.resolution_plan?.process && (
<div className="bg-[#2AAAEA]/10 border-2 border-[#2AAAEA]/40 p-4 rounded-lg mt-4">
<h4 className="font-bold text-cpx-light-blue text-lg mb-3">📋 Resolution Plan - Step-by-Step Process</h4>
<div className="space-y-2 text-sm text-gray-200">
{validationPlan.validations[0].resolution_plan.process.map((step: string, i: number) => (
<div key={i} className="ml-2 leading-relaxed font-medium">{step}</div>
))}
</div>
</div>
)}
                          
{/* Verification Method - Additional detail */}
{validationPlan.validations[0].verificationMethod && (
<div className="bg-[#009245]/10 border border-[#009245]/30 p-3 rounded-lg">
<h4 className="font-semibold text-cpx-green mb-2">🔍 Verification Method:</h4>
<div className="text-sm text-white">
{validationPlan.validations[0].verificationMethod}
</div>
</div>
)}
                          
{/* Potential Issues */}
{validationPlan.validations[0].potentialIssues?.length > 0 && (
<div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 p-3 rounded-lg">
<h4 className="font-semibold text-cpx-yellow mb-2">⚠️ Potential Issues:</h4>
<ul className="text-sm text-gray-200 list-disc list-inside">
{validationPlan.validations[0].potentialIssues.map((issue, i) => (
<li key={i}>{issue}</li>
))}
</ul>
</div>
)}
                          
{/* MONITORING TYPE INDICATOR */}
{validationPlan.validations[0].validation_plan?.strategy && (
<div className={`p-3 rounded-lg border-2 ${
validationPlan.validations[0].validation_plan.strategy.includes('real_time') || 
validationPlan.validations[0].validation_plan.strategy.includes('flash') 
? 'bg-[#FBB03B]/10 border-[#FBB03B]/30' 
: 'bg-[#2AAAEA]/10 border-[#2AAAEA]/30'
}`}>
<div className="font-medium mb-1">
{validationPlan.validations[0].validation_plan.strategy.includes('real_time') || 
validationPlan.validations[0].validation_plan.strategy.includes('flash') ? (
<span className="text-cpx-yellow font-bold uppercase tracking-wider">⚡ CONTINUOUS MONITORING</span>
) : (
<span className="text-cpx-yellow font-bold uppercase tracking-wider">⏰ POST-EVENT RESOLUTION</span>
)}
</div>
<div className={`text-sm ${
validationPlan.validations[0].validation_plan.strategy.includes('real_time') || 
validationPlan.validations[0].validation_plan.strategy.includes('flash') 
? 'text-gray-300' 
: 'text-gray-300'
}`}>
{validationPlan.validations[0].validation_plan.strategy.includes('real_time') || 
validationPlan.validations[0].validation_plan.strategy.includes('flash') ? (
'Betting will stop immediately when condition is detected during the event'
) : (
'Betting closes before event, market resolves after event ends using official results'
)}
</div>
</div>
)}
                          
{/* IMPORTANT WARNING - AFTER THE PLAN */}
<div className="p-4 bg-[#FBB03B]/10 border border-[#FBB03B]/30 rounded-lg">
<p className="text-sm text-white">
<strong>⚠️ IMPORTANT:</strong> This market will resolve EXACTLY as described above. 
Review these details carefully - they determine if you win or lose!
</p>
</div>
                            
{/* PRIMARY VALIDATION METHOD - Show if validation_plan exists */}
{validationPlan.validations[0].validation_plan && (
<>
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B]">
<span className="text-cpx-green font-bold uppercase tracking-wider text-xs">🎯 What will be checked:</span>
<p className="mt-1 text-white font-medium">
{validationPlan.validations[0].validation_plan.primaryMethod?.description || 
validationPlan.validations[0].validation_plan.primaryMethod?.trigger ||
'Automated validation using AI and data sources'}
</p>
</div>
                                
{/* Asset Being Tracked */}
{validationPlan.validations[0].validation_plan.primaryMethod?.symbol && (
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B]">
<span className="text-cpx-yellow font-bold uppercase tracking-wider text-xs">🪙 Asset:</span>
<span className="ml-2 text-cpx-yellow font-black uppercase text-lg">
{validationPlan.validations[0].validation_plan.primaryMethod.symbol}
</span>
</div>
)}
                                
{/* Target Details - Domain Aware */}
{(validationPlan.validations[0].validation_plan.primaryMethod?.targetPrice || 
validationPlan.validations[0].validation_plan.primaryMethod?.targetValue) && (
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B]">
<span className="text-cpx-yellow font-bold uppercase tracking-wider text-xs">
{validationPlan.validations[0].validation_plan.primaryMethod?.displayLabel || '💰 Target:'}
</span>
<span className="ml-2 text-white font-bold text-lg">
{validationPlan.validations[0].validation_plan.primaryMethod?.displayUnit === '$' ? '$' : ''}
{(validationPlan.validations[0].validation_plan.primaryMethod?.targetPrice || 
validationPlan.validations[0].validation_plan.primaryMethod?.targetValue || 0).toLocaleString()}
{validationPlan.validations[0].validation_plan.primaryMethod?.displayUnit && 
validationPlan.validations[0].validation_plan.primaryMethod?.displayUnit !== '$' 
? ` ${validationPlan.validations[0].validation_plan.primaryMethod.displayUnit}` : ''}
</span>
</div>
)}
                                
{/* Continuous Monitoring Info */}
{validationPlan.validations[0].validation_plan.continuousMonitoring?.enabled && (
<div className="bg-[#FBB03B]/10 border border-[#FBB03B]/30 p-4 rounded-lg">
<div className="text-cpx-yellow font-bold mb-2 uppercase tracking-wider text-xs">⚡ Live Monitoring Active</div>
<div className="space-y-2 text-sm">
<div>
<span className="text-cpx-yellow font-bold">Watching:</span>
<span className="ml-2 text-white">
{validationPlan.validations[0].validation_plan.continuousMonitoring.watchMethod?.api || 'Real-time data feed'}
</span>
</div>
<div>
<span className="text-cpx-yellow font-bold">Frequency:</span>
<span className="ml-2 text-white">
{validationPlan.validations[0].validation_plan.continuousMonitoring.watchMethod?.frequency?.replace('every_', 'Every ').replace('_', ' ') || 'Real-time'}
</span>
</div>
<div>
<span className="text-cpx-yellow font-bold">Will stop betting when:</span>
<span className="ml-2 text-white">
{validationPlan.validations[0].validation_plan.continuousMonitoring.stopMarketWhen || 'Condition is met'}
</span>
</div>
{validationPlan.validations[0].validation_plan.continuousMonitoring.immediateStop && (
<div className="text-xs text-cpx-yellow italic font-bold">
⚡ Market will close immediately when condition is detected
</div>
)}
</div>
</div>
)}
                                
{/* Data Source */}
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B]">
<span className="text-cpx-light-blue font-bold uppercase tracking-wider text-xs">📊 Data Source:</span>
<span className="ml-2 text-white font-bold">
{(validationPlan.validations[0].validation_plan.primaryMethod?.api || 
validationPlan.validations[0].validation_plan.dataSourcePriority?.[0]?.source ||
'Multiple AI-verified sources')
.replace(/perplexity_search/gi, 'Web Search')
.replace(/perplexity/gi, 'Web Search')}
</span>
</div>
                                
{/* Resolution Criteria */}
{validationPlan.validations[0].validation_plan.resolutionCriteria && (
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B]">
<div className="text-cpx-green font-bold mb-2 uppercase tracking-wider text-xs">✅ Resolution Rules:</div>
<div className="space-y-2 ml-2">
<div>
<span className="inline-block bg-[#009245]/30 text-white px-3 py-1 rounded-lg text-sm font-black mr-2 border border-[#009245]">YES</span>
<span className="text-white text-sm">
{validationPlan.validations[0].validation_plan.resolutionCriteria.positiveMatch}
</span>
</div>
<div>
<span className="inline-block bg-red-600/30 text-white px-3 py-1 rounded-lg text-sm font-black mr-2 border border-red-500">NO</span>
<span className="text-white text-sm">
{validationPlan.validations[0].validation_plan.resolutionCriteria.negativeMatch}
</span>
</div>
</div>
</div>
)}
                                
{/* Methodology from validation_plan */}
{validationPlan.validations[0].validation_plan.resolutionCriteria?.methodology && (
<div className="bg-[#052393]/40 p-4 rounded-lg border-2 border-[#FBB03B]">
<span className="text-cpx-yellow font-bold uppercase tracking-wider text-xs">📋 Methodology:</span>
<p className="mt-1 text-white text-sm whitespace-pre-wrap">
{validationPlan.validations[0].validation_plan.resolutionCriteria.methodology}
</p>
</div>
)}
</>
)}
                            
<div className="border-t pt-2">
<div className="flex justify-between">
<span className="text-cpx-green font-bold">Confidence:</span>
<span className="font-medium">{validationPlan.validations[0].confidence}%</span>
</div>
<div className="flex justify-between">
<span className="text-gray-400 font-bold">Created:</span>
<span className="text-xs">{new Date(validationPlan.validations[0].timestamp).toLocaleString()}</span>
</div>
</div>
</>
) : (
/* Fallback to old display if validation_plan not available */
<>
<div>
<span className="text-cpx-yellow font-bold">Decision:</span>
<span className={`ml-2 px-2 py-1 rounded text-xs ${
validationPlan?.validations?.[0]?.decision === 'APPROVE' 
? 'bg-[#009245]/30 text-white border border-[#009245]' 
: 'bg-red-600/30 text-white border border-red-500'
}`}>
{validationPlan?.validations?.[0]?.decision || 'N/A'}
</span>
</div>
<div>
<span className="text-gray-400 font-bold">Reasoning:</span>
<p className="mt-1 text-white bg-[#052393]/20 p-2 rounded">
{validationPlan?.validations?.[0]?.reasoning || 'No reasoning provided'}
</p>
</div>
</>
)}
</div>
</div>

{/* Market Information - Moved to bottom */}
<div className="bg-[#052393]/20 border border-[#052393]/40 p-4 rounded-lg">
<h4 className="font-bold text-cpx-light-blue mb-3 uppercase tracking-wider text-sm">💼 Market Information</h4>
<div className="grid grid-cols-2 gap-4 text-sm">
<div>
<span className="text-gray-300">Market ID:</span>
<span className="ml-2 font-mono text-xs text-white">{validationPlan?.market?.id || market?.id || 'N/A'}</span>
</div>
<div>
<span className="text-gray-300">Status:</span>
<span className={`ml-2 px-2 py-1 rounded text-xs ${
(validationPlan?.market?.status || market?.status) === 'active' ? 'bg-[#009245]/30 text-white border border-[#009245]' : 'bg-[#052393]/30 text-gray-300 border border-[#052393]'
}`}>
{validationPlan?.market?.status || market?.status || 'Unknown'}
</span>
</div>
<div>
<span className="text-gray-300">Created:</span>
<span className="ml-2 text-white">{market?.created_at || market?.createdAt ? new Date(market.created_at || market.createdAt).toLocaleDateString() : 'N/A'}</span>
</div>
<div>
<span className="text-gray-300">Total Volume:</span>
<span className="ml-2 font-medium text-white">{((market?.totalYes || 0) + (market?.totalNo || 0)).toLocaleString()} tokens</span>
</div>
</div>
</div>

{/* Resolution Stats */}
<div className="bg-[#009245]/10 border border-[#009245]/30 p-4 rounded-lg">
<h4 className="font-black text-cpx-green mb-3 uppercase tracking-wider text-sm">📊 Resolution Statistics</h4>
<div className="grid grid-cols-2 gap-4 text-sm">
<div>
<span className="text-gray-300">Total Validations:</span>
<span className="ml-2 font-medium text-white">{validationPlan?.resolution?.totalCalls || 0}</span>
</div>
<div>
<span className="text-gray-300">Success Rate:</span>
<span className="ml-2 font-medium text-cpx-green">{validationPlan?.resolution?.successRate || 0}%</span>
</div>
<div>
<span className="text-gray-300">Evidence Count:</span>
<span className="ml-2 font-medium text-white">{validationPlan?.resolution?.evidence?.length || 0}</span>
</div>
<div>
<span className="text-gray-300">Tool Usage:</span>
<span className="ml-2 font-medium text-white">{validationPlan?.resolution?.toolUsage?.length || 0}</span>
</div>
</div>
</div>

{/* Raw Data Toggle */}
<details className="bg-[#001a3d] border border-[#052393]/40 rounded-lg">
<summary className="p-4 cursor-pointer hover:bg-[#052393]/20 rounded-lg font-medium text-gray-300 transition-colors">
🔧 View Raw Data (Developer Mode)
</summary>
<div className="p-4 border-t border-[#052393]/40">
<pre className="text-xs text-gray-300 overflow-auto bg-[#001233] p-3 rounded">
{JSON.stringify(validationPlan, null, 2)}
</pre>
</div>
</details>
</div>
</div>
</div>
              
<div className="flex justify-end mt-6">
<button
onClick={() => setShowValidationPlan(false)}
className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
>
Close
</button>
</div>
</div>
</div>
        )}
      
      {/* Resolution Modal - Auto-shows when market resolves */}
      {showResolutionModal && market && (
        <ResolutionModal
          market={market}
          evidence={resolutionEvidence}
          userWallet={publicKey?.toString() || null}
          onClose={() => setShowResolutionModal(false)}
          onClaim={async () => {
            try {
              toast.loading('Claiming winnings...', { id: 'claim' })
                            
              // Using connection from useConnection() hook
              const programId = new PublicKey('2Jso3cMEDPjQYx1nKsWPsCkJzzAj8kpVsTwq4PSRPGV1')
              const tokenMint = new PublicKey('5mbkMp8x4aZ9ntsVPPnVUFekiE1joGU4gSANfsTtiiaT')
                            
              const marketIdNum = parseInt(id || '0')
              const marketIdBytes = new Uint8Array(8)
              new DataView(marketIdBytes.buffer).setBigUint64(0, BigInt(marketIdNum), true)
                            
              const [configPDA] = PublicKey.findProgramAddressSync([Buffer.from("config")], programId)
              const [marketPDA] = PublicKey.findProgramAddressSync([Buffer.from("market"), marketIdBytes.slice(0, 6)], programId)
              const [userPositionPDA] = PublicKey.findProgramAddressSync([Buffer.from("position"), marketPDA.toBuffer(), publicKey!.toBuffer()], programId)
              const [yesVaultAuth] = PublicKey.findProgramAddressSync([Buffer.from("yes_vault_auth"), marketPDA.toBuffer()], programId)
              const [noVaultAuth] = PublicKey.findProgramAddressSync([Buffer.from("no_vault_auth"), marketPDA.toBuffer()], programId)
                            
              const yesVault = await getAssociatedTokenAddress(tokenMint, yesVaultAuth, true)
              const noVault = await getAssociatedTokenAddress(tokenMint, noVaultAuth, true)
              const userTokenAccount = await getAssociatedTokenAddress(tokenMint, publicKey!)
                            
              const discriminator = Buffer.from([161, 215, 24, 59, 14, 236, 242, 221])
                            
              const accounts = [
              { pubkey: configPDA, isSigner: false, isWritable: false },
              { pubkey: marketPDA, isSigner: false, isWritable: true },
              { pubkey: yesVaultAuth, isSigner: false, isWritable: false },
              { pubkey: noVaultAuth, isSigner: false, isWritable: false },
              { pubkey: yesVault, isSigner: false, isWritable: true },
              { pubkey: noVault, isSigner: false, isWritable: true },
              { pubkey: tokenMint, isSigner: false, isWritable: false },
              { pubkey: publicKey!, isSigner: true, isWritable: true },
              { pubkey: userPositionPDA, isSigner: false, isWritable: true },
              { pubkey: userTokenAccount, isSigner: false, isWritable: true },
              { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false }
              ]
                            
              const claimInstruction = new TransactionInstruction({
              keys: accounts,
              programId,
              data: discriminator
              })
                            
              const transaction = new Transaction().add(claimInstruction)
              transaction.feePayer = publicKey!
              const { blockhash } = await connection.getLatestBlockhash()
              transaction.recentBlockhash = blockhash
                            
              const signedTx = await (window as any).solana.signTransaction(transaction)
              const signature = await connection.sendRawTransaction(signedTx.serialize())
              const confirmation = await connection.confirmTransaction(signature, 'confirmed')
                            
              if (confirmation.value.err) {
              throw new Error('Claim failed')
              }
                            
              console.log('✅ Winnings claimed from blockchain!')
                            
              // Update database to record the claim
              try {
              await axios.post(`/api/markets/${id}/claim`, {
              walletAddress: publicKey!.toString(),
              signature
              })
              console.log('✅ Claim recorded in database')
              } catch (dbError) {
              console.error('⚠️ Failed to record claim in database:', dbError)
              // Don't fail the whole claim - blockchain succeeded
              }
                            
              toast.success('Winnings claimed!', { id: 'claim' })
              setShowResolutionModal(false)
                            
              } catch (error: any) {
              toast.error(error.message || 'Failed to claim', { id: 'claim' })
              }
              }}
          />
      )}

      {/* AI ODDS REASONING MODAL */}
      {showOddsReasoningModal && market?.odds_reasoning_parsed && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowOddsReasoningModal(false)}>
          <div className="bg-[#001233] border border-[#2AAAEA]/40 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-2xl font-black text-white">🎲 Initial AI Odds Analysis</h2>
                <p className="text-sm text-gray-400 mt-1">AI's recommendation when this market was created</p>
              </div>
              <button onClick={() => setShowOddsReasoningModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Odds Display */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-green-500/10 p-4 rounded-lg border border-green-500/30 text-center">
                <div className="text-sm text-gray-400 mb-2">INITIAL YES</div>
                <div className="text-4xl font-black text-green-400">
                  {Math.round((market.ai_odds_yes || 0.5) * 100)}%
                </div>
              </div>
              <div className="bg-red-500/10 p-4 rounded-lg border border-red-500/30 text-center">
                <div className="text-sm text-gray-400 mb-2">INITIAL NO</div>
                <div className="text-4xl font-black text-red-400">
                  {Math.round((market.ai_odds_no || 0.5) * 100)}%
                </div>
              </div>
            </div>

            {/* Confidence */}
            <div className="bg-[#052393]/20 p-4 rounded-lg border border-[#2AAAEA]/30 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-gray-400">AI Confidence</span>
                <span className="text-xl font-bold text-white">{market.odds_confidence || 0}%</span>
              </div>
            </div>

            {/* Reasoning */}
            {market.odds_reasoning_parsed.reasoning && (
              <div className="bg-[#001233] p-4 rounded-lg border border-[#052393]/40 mb-6">
                <h3 className="text-sm font-bold text-white mb-3">📊 AI Reasoning</h3>
                <p className="text-gray-300 text-sm whitespace-pre-line">{market.odds_reasoning_parsed.reasoning}</p>
              </div>
            )}

            {/* Research Summary */}
            {market.odds_reasoning_parsed.researchSummary?.keyFactors && (
              <div className="bg-[#052393]/10 p-4 rounded-lg border border-[#2AAAEA]/20 mb-6">
                <h3 className="text-sm font-bold text-white mb-3">🔍 Key Factors</h3>
                <ul className="space-y-2">
                  {market.odds_reasoning_parsed.researchSummary.keyFactors.map((factor: string, i: number) => (
                    <li key={i} className="text-gray-300 text-sm flex items-start">
                      <span className="text-[#2AAAEA] mr-2">•</span>
                      <span>{factor}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Sources */}
            {market.odds_sources_parsed && market.odds_sources_parsed.length > 0 && (
              <div className="bg-gray-900/50 p-4 rounded-lg">
                <h3 className="text-sm font-bold text-white mb-3">📚 Data Sources</h3>
                <div className="space-y-2">
                  {market.odds_sources_parsed.map((source: any, i: number) => (
                    <div key={i} className="text-sm">
                      <span className="text-gray-400">{source.type}:</span>
                      <span className="text-white ml-2">{source.source}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Timestamp */}
            {market.odds_updated_at && (
              <div className="text-xs text-gray-500 text-center mt-6">
                Analysis updated: {new Date(market.odds_updated_at).toLocaleString()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}